/* ============================================================
 * View + controller layer (canvas rendering, DOM, input).
 * Simulation state and physics live in ./engine/engine-core.js.
 * ============================================================
 */ // Main-thread proxy for the simulation engine running in a Web Worker.
//
// Owns the Worker, exposes a small command API, and surfaces the latest
// snapshot via the onSnapshot callback. All communication is asynchronous;
// the view never blocks on the worker.
var $27b56ff3a49e4887$exports = {};
var $56b621a6dd25b2cb$exports = {};
"use strict";
$56b621a6dd25b2cb$exports = function(workerUrl, origin, isESM) {
    if (origin === self.location.origin) // If the worker bundle's url is on the same origin as the document,
    // use the worker bundle's own url.
    return workerUrl;
    else {
        // Otherwise, create a blob URL which loads the worker bundle with `importScripts`.
        var source = isESM ? 'import ' + JSON.stringify(workerUrl) + ';' : 'importScripts(' + JSON.stringify(workerUrl) + ');';
        return URL.createObjectURL(new Blob([
            source
        ], {
            type: 'application/javascript'
        }));
    }
};


let $27b56ff3a49e4887$var$url = new URL(import.meta.resolve("by0DS"));
$27b56ff3a49e4887$exports = $56b621a6dd25b2cb$exports($27b56ff3a49e4887$var$url.toString(), $27b56ff3a49e4887$var$url.origin, true);


class $e711b824db086d10$export$8afa9d3005acea26 {
    constructor(){
        this.worker = new Worker($27b56ff3a49e4887$exports);
        this.onSnapshot = null;
        this.worker.onmessage = (e)=>{
            const msg = e.data || {};
            if (msg.type === 'snapshot' && this.onSnapshot) this.onSnapshot(msg);
        };
    }
    _post(msg) {
        this.worker.postMessage(msg);
    }
    init(ownShip, tracks, clock = 0) {
        this._post({
            type: 'init',
            ownShip: ownShip,
            tracks: tracks,
            clock: clock
        });
    }
    setRunning(running) {
        this._post({
            type: 'setRunning',
            running: running
        });
    }
    setSpeed(simSpeed) {
        this._post({
            type: 'setSpeed',
            simSpeed: simSpeed
        });
    }
    editOwnShip(field, value) {
        this._post({
            type: 'editOwnShip',
            field: field,
            value: value
        });
    }
    editTrack(id, field, value) {
        this._post({
            type: 'editTrack',
            id: id,
            field: field,
            value: value
        });
    }
    addTrack(track) {
        this._post({
            type: 'addTrack',
            track: track
        });
    }
    dropTrack(id) {
        this._post({
            type: 'dropTrack',
            id: id
        });
    }
    dragStart(id) {
        this._post({
            type: 'dragStart',
            id: id
        });
    }
    dragEnd(id) {
        this._post({
            type: 'dragEnd',
            id: id
        });
    }
    destroy() {
        this.worker.terminate();
        this.worker = null;
    }
}


// Minimal IndexedDB wrapper for persisting a single simulator session, so the
// app can resume exactly where the user left off after being closed and
// reopened offline. One database, one object store, one record.
//
// All operations are best-effort: callers wrap them in try/catch and fall back
// to a fresh scenario if storage is unavailable or the record is unreadable.
const $b6828afa4dfd334d$var$DB_NAME = 'maneuver';
const $b6828afa4dfd334d$var$STORE = 'session';
const $b6828afa4dfd334d$var$KEY = 'current';
const $b6828afa4dfd334d$var$DB_VERSION = 1;
function $b6828afa4dfd334d$var$openDb() {
    return new Promise((resolve, reject)=>{
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB unavailable'));
            return;
        }
        const req = indexedDB.open($b6828afa4dfd334d$var$DB_NAME, $b6828afa4dfd334d$var$DB_VERSION);
        req.onupgradeneeded = ()=>{
            const db = req.result;
            if (!db.objectStoreNames.contains($b6828afa4dfd334d$var$STORE)) db.createObjectStore($b6828afa4dfd334d$var$STORE);
        };
        req.onsuccess = ()=>resolve(req.result);
        req.onerror = ()=>reject(req.error);
    });
}
async function $b6828afa4dfd334d$export$ccdf34816785229e(data) {
    const db = await $b6828afa4dfd334d$var$openDb();
    try {
        await new Promise((resolve, reject)=>{
            const tx = db.transaction($b6828afa4dfd334d$var$STORE, 'readwrite');
            tx.objectStore($b6828afa4dfd334d$var$STORE).put(data, $b6828afa4dfd334d$var$KEY);
            tx.oncomplete = resolve;
            tx.onerror = ()=>reject(tx.error);
            tx.onabort = ()=>reject(tx.error);
        });
    } finally{
        db.close();
    }
}
async function $b6828afa4dfd334d$export$fa0b40e553fd7792() {
    const db = await $b6828afa4dfd334d$var$openDb();
    try {
        return await new Promise((resolve, reject)=>{
            const tx = db.transaction($b6828afa4dfd334d$var$STORE, 'readonly');
            const req = tx.objectStore($b6828afa4dfd334d$var$STORE).get($b6828afa4dfd334d$var$KEY);
            req.onsuccess = ()=>resolve(req.result || null);
            req.onerror = ()=>reject(req.error);
        });
    } finally{
        db.close();
    }
}
async function $b6828afa4dfd334d$export$7809a8af6f3e4c63() {
    const db = await $b6828afa4dfd334d$var$openDb();
    try {
        await new Promise((resolve, reject)=>{
            const tx = db.transaction($b6828afa4dfd334d$var$STORE, 'readwrite');
            tx.objectStore($b6828afa4dfd334d$var$STORE).delete($b6828afa4dfd334d$var$KEY);
            tx.oncomplete = resolve;
            tx.onerror = ()=>reject(tx.error);
        });
    } finally{
        db.close();
    }
}


/* ============================================================
 * Radar visual configuration.
 *
 * Central source of truth for the radar's visual styling: stroke
 * weights, opacity, dash patterns, and selected/alert-state settings.
 * Values are semantic (what a mark means in the hierarchy), not tied to
 * any particular element, so the rendering code imports these instead of
 * embedding magic numbers. Changing the radar's look should only require
 * edits here. These constants describe styling only — they do not affect
 * radar geometry, calculations, or interaction behavior.
 * ============================================================
 */ /*
 * Stroke weights in CSS px, defining the radar's visual hierarchy from
 * least to most prominent (scaled by devicePixelRatio at draw time, see
 * Simulator.strokeWidth):
 *
 *   auxiliary 0.5  <  reference 0.75  <  overlay 1  <  object 1.25
 *     <  vector 1.5  <  focus 2  <  alert 2.5
 *
 * Each tier MUST stay strictly heavier than the one below it: that ordering
 * is what keeps important marks legible. Background reference geometry and
 * projections recede; target symbols and vectors sit above them; ownship and
 * the selected target read as focus; warning targets are heaviest. Collapsing
 * or reordering these values flattens the hierarchy and buries the objects
 * that matter — verifyStrokeHierarchy() (below) guards the invariant.
 */ const $759706aefe3a0ba6$export$592a985c58ff6f5f = 0.75; // range rings, bearing ticks, static grid geometry
const $759706aefe3a0ba6$export$d6f846fb845aace5 = 0.5; // dotted projection lines, trails, secondary guide lines
const $759706aefe3a0ba6$export$48db466bbdac31ce = 1; // analytical overlays — selected-target CPA / closest-point line
const $759706aefe3a0ba6$export$c1e52ab67dc1f5d5 = 1.25; // target boxes / symbols
const $759706aefe3a0ba6$export$bcd43da01965fd39 = 1.5; // target course/speed (and wind) vectors
const $759706aefe3a0ba6$export$b03235fd9a94c4b6 = 2; // ownship, selected target outline + vector
const $759706aefe3a0ba6$export$cf5b4658614cc12 = 2.5; // collision-risk / warning-state targets
const $759706aefe3a0ba6$export$ca4d951bcf1370f = {
    reference: $759706aefe3a0ba6$export$592a985c58ff6f5f,
    auxiliary: $759706aefe3a0ba6$export$d6f846fb845aace5,
    overlay: $759706aefe3a0ba6$export$48db466bbdac31ce,
    object: $759706aefe3a0ba6$export$c1e52ab67dc1f5d5,
    vector: $759706aefe3a0ba6$export$bcd43da01965fd39,
    focus: $759706aefe3a0ba6$export$b03235fd9a94c4b6,
    alert: $759706aefe3a0ba6$export$cf5b4658614cc12
};
const $759706aefe3a0ba6$export$cda74e8e8c93ae9e = [
    'auxiliary',
    'reference',
    'overlay',
    'object',
    'vector',
    'focus',
    'alert'
];
function $759706aefe3a0ba6$export$53426cae529b83e8() {
    for(let i = 1; i < $759706aefe3a0ba6$export$cda74e8e8c93ae9e.length; i++){
        const lighter = $759706aefe3a0ba6$export$cda74e8e8c93ae9e[i - 1];
        const heavier = $759706aefe3a0ba6$export$cda74e8e8c93ae9e[i];
        if (!($759706aefe3a0ba6$export$ca4d951bcf1370f[heavier] > $759706aefe3a0ba6$export$ca4d951bcf1370f[lighter])) return `radar stroke hierarchy flattened: '${heavier}' (${$759706aefe3a0ba6$export$ca4d951bcf1370f[heavier]}) must be heavier than '${lighter}' (${$759706aefe3a0ba6$export$ca4d951bcf1370f[lighter]})`;
    }
    return null;
}
const $759706aefe3a0ba6$export$9541d2d7326be049 = 0.6;
const $759706aefe3a0ba6$export$34cfc868cef4d0b5 = 0.75;
const $759706aefe3a0ba6$export$cab7f44c7a8c34dc = 0.6;
const $759706aefe3a0ba6$export$50ae0dfaa401c539 = {
    solid: [],
    bearingTick: [
        4,
        4
    ],
    cpaLine: [
        2,
        3
    ],
    bearingLine: [
        2,
        4
    ],
    relativeMotion: [
        5,
        5
    ],
    wind: [
        5,
        5
    ]
};
const $759706aefe3a0ba6$export$65f5e322c6eb47c1 = 1.35;
const $759706aefe3a0ba6$export$718af19761f21695 = 1.2;
const $759706aefe3a0ba6$export$1cb94782fae13269 = '!';


// --- Polar grid rendering constants ---
const $8b46766143819e9f$var$CARDINAL_BEARINGS = [
    0,
    90,
    180,
    270
];
const $8b46766143819e9f$var$LABEL_OFFSET_PX = 6; // gap between ring and label
const $8b46766143819e9f$var$fsApi = {
    request (el = document.documentElement) {
        return el.requestFullscreen?.() || el.webkitRequestFullscreen?.() || el.mozRequestFullScreen?.() || el.msRequestFullscreen?.();
    },
    exit () {
        return document.exitFullscreen?.() || document.webkitExitFullscreen?.() || document.mozCancelFullScreen?.() || document.msExitFullscreen?.();
    },
    isActive () {
        return document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
    }
};
class $8b46766143819e9f$export$b14c74960ae6f55e {
    constructor(config = {}){
        // --- Configuration & Mode ---
        this.config = config;
        console.log('Simulator initialized in mode:', this.config.mode || 'default');
        // --- Suppress rendering flag for editable fields ---
        this.suppressEditRender = false;
        // Flag and helper for debounced UI updates
        this.uiUpdatePending = false;
        // --- DOM Element References ---
        this.canvas = document.getElementById('radarCanvas');
        this.ctx = this.canvas.getContext('2d');
        if (!this.canvas) console.error('radarCanvas element not found in DOM');
        // console.log('Simulator init: 2D context:', this.ctx); // Reduced noise
        if (!this.ctx) console.error('Failed to get 2D context for radarCanvas');
        this.dragTooltip = document.getElementById('drag-tooltip');
        this.orderTooltip = document.getElementById('order-tooltip');
        // Playback controls
        this.btnPlayPause = document.getElementById('play-pause');
        this.btnRange = document.getElementById('radar-range');
        this.btnAddTrack = document.getElementById('add-track');
        this.btnDropTrack = document.getElementById('remove-track');
        this.btnScen = document.getElementById('regenerate');
        this.btnFuture = document.getElementById('future');
        this.btnPast = document.getElementById('past');
        this.ffSpeedIndicator = document.getElementById('ff-speed-indicator');
        this.revSpeedIndicator = document.getElementById('rev-speed-indicator');
        this.trackDataContainer = document.getElementById('track-data-container');
        this.rmDataContainer = document.getElementById('rm-data-container');
        this.cpaDataContainer = document.getElementById('cpa-data-container');
        this.windDataContainer = document.getElementById('wind-data-container');
        this.simClock = document.getElementById('sim-clock');
        this.mainContainer = document.querySelector('main.radar');
        // V2 HTML uses <main class="radar"> as container; alias for wrapper
        this.radarWrapper = this.mainContainer;
        this.btnSettings = document.getElementById('radar-settings');
        this.settingsDrawer = document.getElementById('radar-settings-menu');
        this.chkPolarPlot = document.getElementById('polar-plots');
        this.chkTrackIds = document.getElementById('track-ids');
        this.btnFullscreen = document.getElementById('full-screen');
        // --- Configuration ---
        this.radarGreen = getComputedStyle(document.documentElement).getPropertyValue('--radar-green').trim();
        this.radarWhite = getComputedStyle(document.documentElement).getPropertyValue('--radar-white').trim();
        this.radarFaintGreen = getComputedStyle(document.documentElement).getPropertyValue('--radar-faint-green').trim();
        this.radarFaintWhite = getComputedStyle(document.documentElement).getPropertyValue('--radar-faint-white').trim();
        this.radarDarkOrange = getComputedStyle(document.documentElement).getPropertyValue('--radar-dark-orange').trim();
        // --- State Data ---
        this.ownShip = {
            course: 91,
            speed: 12.7,
            id: 'ownShip',
            x: 0,
            y: 0,
            orderedCourse: 91,
            orderedSpeed: 12.7,
            dragCourse: null,
            dragSpeed: null,
            orderedVectorEndpoint: null
        };
        this.tracks = [
            {
                id: '01',
                initialBearing: 327,
                initialRange: 7.9,
                course: 255,
                speed: 6.1
            },
            {
                id: '02',
                initialBearing: 345,
                initialRange: 6.5,
                course: 250,
                speed: 7.2
            },
            {
                id: '03',
                initialBearing: 190,
                initialRange: 8.2,
                course: 75,
                speed: 8.0
            },
            {
                id: '04',
                initialBearing: 205,
                initialRange: 5.5,
                course: 70,
                speed: 7.5
            },
            {
                id: '05',
                initialBearing: 180,
                initialRange: 3.1,
                course: 72,
                speed: 8.2
            }
        ];
        this.selectedTrackId = '01';
        this.hoveredTrackId = null;
        this.draggedItemId = null;
        this.dragType = null;
        this.pendingDragId = null;
        this.pendingDragType = null;
        this.DPR = window.devicePixelRatio || 1;
        this.pointerDownPos = {
            x: 0,
            y: 0
        };
        this.dragThreshold = 6 * this.DPR;
        this.LONG_PRESS_MS = 450;
        this._longPress = null; // { timer, x, y } while a long-press is pending
        this._ghostContact = null; // preview contact being placed via long-press + drag
        this._activePointers = new Map(); // pointerId -> screen {x, y}, for pinch
        this._pinching = false;
        this._pinchPrev = null; // { mid, dist } from the previous pinch move
        // Inspection magnifier: a view camera over the radar (1 = fit, >1 = zoomed in).
        this.viewScale = 1;
        this.viewOffset = {
            x: 0,
            y: 0
        }; // device-px translation applied before scale
        this.MAX_VIEW_SCALE = 6;
        // Dev guardrail: surface any accidental flattening of the radar stroke hierarchy.
        const strokeHierarchyError = (0, $759706aefe3a0ba6$export$53426cae529b83e8)();
        if (strokeHierarchyError) console.warn(`[radarVisualConfig] ${strokeHierarchyError}`);
        this.lastMousePos = {
            x: 0,
            y: 0
        };
        this.lastTimestamp = 0;
        this.lastDomUpdate = 0;
        this.DOM_UPDATE_INTERVAL = 200;
        this.DRAG_SYNC_MS = 50; // throttle interval for streaming drags to the worker
        this._dragPending = null;
        this._dragFlushTimer = null;
        this._dragLastSent = 0;
        this.SAVE_DEBOUNCE_MS = 2000; // throttle interval for persisting the session
        this._saveTimer = null;
        this._onVisibilityChange = ()=>{
            if (document.visibilityState === 'hidden') this._saveSessionNow();
        };
        this._onPageHide = ()=>this._saveSessionNow();
        this.sceneDirty = true;
        this.simulationElapsed = 0;
        this.activeEditField = null;
        // --- Weather Data ---
        this.trueWind = {
            direction: 70,
            speed: 15,
            wPos: {
                x: 0,
                y: 0
            },
            arrowEndpoint: {
                x: 0,
                y: 0
            }
        };
        this.relativeWind = {};
        // --- Feature Toggle States ---
        this.maxRange = 12.0;
        this.rangeScales = [
            3.0,
            6.0,
            12.0,
            24.0
        ];
        this.vectorTimeInMinutes = 15;
        this.vectorTimes = [
            3,
            15,
            30
        ];
        // Indexes for cycling vector time and range (ensure label updates immediately)
        this.vectorTimeIndex = this.vectorTimes.indexOf(this.vectorTimeInMinutes);
        this.rangeIndex = this.rangeScales.indexOf(this.maxRange);
        this.simulationSpeed = 1;
        this.ffSpeeds = [
            25,
            50
        ];
        this.revSpeeds = [
            -25,
            -50
        ];
        this.showRelativeMotion = true;
        this.showCPAInfo = true;
        this.isSimulationRunning = true;
        this.showWeather = false;
        this.showPolarPlot = true;
        this.showTrackIds = true;
        this.uiScaleFactor = 1;
        // Sync data panel visibility with feature toggles
        this.rmDataContainer.open = this.showRelativeMotion;
        this.cpaDataContainer.open = this.showCPAInfo;
        this.windDataContainer.open = this.showWeather;
        // Pre-rendered radar backdrop
        this.staticCanvas = document.createElement('canvas');
        this.staticCtx = this.staticCanvas.getContext('2d');
        this.staticDirty = true;
        // The authoritative simulation engine runs in a Web Worker. The view
        // keeps a local render model (ownShip/tracks) that is updated from the
        // worker's snapshots; user input is sent to the worker as commands and
        // applied optimistically to the local model for immediate feedback.
        this.engineClient = null;
        this._pendingSnapshot = null;
        // Bind methods to ensure correct `this` context
        this.gameLoop = this.gameLoop.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handlePointerMove = this.handlePointerMove.bind(this);
        this._initialize();
    }
    // --- Main Initialization ---
    async _initialize() {
        this._attachEventListeners();
        // Resume a previously saved session if one exists (offline-first), else
        // start from the default scenario. Loading IndexedDB is async, so the
        // first render happens once this resolves. A "fresh" config (used by the
        // regenerate button) skips restore and wipes any saved session.
        let restored = false;
        if (this.config.fresh) try {
            await (0, $b6828afa4dfd334d$export$7809a8af6f3e4c63)();
        } catch (err) {}
        else try {
            const saved = await (0, $b6828afa4dfd334d$export$fa0b40e553fd7792)();
            if (this._destroyed) return;
            restored = this._applyRestoredSession(saved);
        } catch (err) {
            restored = false;
        }
        if (this._destroyed) return;
        if (!restored) {
            // Seed the two additional starting tracks (the app starts with the 5
            // default tracks plus 2 generated ones; selection lands on the last).
            this._seedInitialExtraTracks(2);
            this.tracks.forEach((track)=>{
                if (track.initialBearing !== undefined && track.initialRange !== undefined) {
                    track.x = this.ownShip.x + track.initialRange * Math.sin(this.toRadians(track.initialBearing));
                    track.y = this.ownShip.y + track.initialRange * Math.cos(this.toRadians(track.initialBearing));
                } else if (track.x !== undefined && track.y !== undefined) {
                    const dx = track.x - this.ownShip.x;
                    const dy = track.y - this.ownShip.y;
                    track.initialRange = Math.sqrt(dx ** 2 + dy ** 2);
                    track.initialBearing = (this.toDegrees(Math.atan2(dx, dy)) + 360) % 360;
                }
            });
        }
        // Hand the authoritative state to the worker engine and subscribe to
        // its snapshots. The worker integrates physics + COLREGs off the main
        // thread; the view renders the latest snapshot via the rAF loop.
        this.engineClient = new (0, $e711b824db086d10$export$8afa9d3005acea26)();
        this.engineClient.onSnapshot = (snap)=>{
            this._pendingSnapshot = snap;
            this.markSceneDirty();
        };
        this.engineClient.init(this._serializeOwnShip(), this.tracks.map((t)=>this._serializeTrack(t)), this.simulationElapsed);
        this.engineClient.setSpeed(this.simulationSpeed);
        this.engineClient.setRunning(this.isSimulationRunning);
        this.tracks.forEach((t)=>this.calculateAllData(t));
        this.calculateWindData();
        this.updateButtonStyles();
        this.updateSpeedIndicator();
        this.updatePanelsAndRedraw();
        this.scaleUI();
        this.lastTimestamp = performance.now();
        if (this.isSimulationRunning) this.startGameLoop();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(()=>this.scaleUI());
    }
    // Builds n extra starting tracks (same shape/logic as addTrack but without
    // touching the worker, which is initialised once afterwards with all state).
    _seedInitialExtraTracks(n) {
        for(let i = 0; i < n; i++){
            const existingNums = this.tracks.map((t)=>parseInt(t.id, 10)).sort((a, b)=>a - b);
            let newNum = 1;
            while(existingNums.includes(newNum))newNum++;
            const newId = String(newNum).padStart(4, '0');
            const newTrack = {
                id: newId,
                initialBearing: Math.random() * 360,
                initialRange: this.maxRange * (0.1 + Math.random() * 0.8),
                course: Math.random() * 360,
                speed: 2 + Math.random() * 13
            };
            newTrack.x = this.ownShip.x + newTrack.initialRange * Math.sin(this.toRadians(newTrack.initialBearing));
            newTrack.y = this.ownShip.y + newTrack.initialRange * Math.cos(this.toRadians(newTrack.initialBearing));
            this.tracks.push(newTrack);
            this.selectedTrackId = newId;
        }
    }
    // --- Worker state serialization helpers ---
    _serializeOwnShip() {
        const o = this.ownShip;
        return {
            id: o.id,
            x: o.x,
            y: o.y,
            course: o.course,
            speed: o.speed,
            orderedCourse: o.orderedCourse,
            orderedSpeed: o.orderedSpeed
        };
    }
    _serializeTrack(t) {
        return {
            id: t.id,
            x: t.x,
            y: t.y,
            course: t.course,
            speed: t.speed,
            initialBearing: t.initialBearing,
            initialRange: t.initialRange,
            state: t.state,
            _base: t._base,
            isHazard: !!t.isHazard,
            isUserControlled: !!t.isUserControlled
        };
    }
    // Merge an authoritative snapshot into the local render model. View-only
    // computed fields (range, bearing, rmVector, cpa, vectorEndpoint, ...) are
    // preserved and recomputed by calculateAllData; the dragged track is left
    // untouched so optimistic drag motion is not overridden.
    _applySnapshot(snap) {
        const state = snap.state;
        if (!state || !Array.isArray(state.tracks) || !this.tracks) return;
        this.simulationElapsed = snap.clock;
        // Rewind auto-stop: when the clock reaches zero while rewinding, resume
        // normal forward play (mirrors the original single-thread behavior).
        if (snap.clock <= 0 && this.simulationSpeed < 0) {
            this.simulationSpeed = 1;
            this.engineClient.setSpeed(1);
            this.updateSpeedIndicator();
            this.updateButtonStyles();
        }
        const o = state.ownShip;
        if (o && this.ownShip) {
            this.ownShip.x = o.x;
            this.ownShip.y = o.y;
            this.ownShip.course = o.course;
            this.ownShip.speed = o.speed;
            this.ownShip.orderedCourse = o.orderedCourse;
            this.ownShip.orderedSpeed = o.orderedSpeed;
        }
        const byId = new Map(this.tracks.map((t)=>[
                t.id,
                t
            ]));
        const nextIds = new Set();
        for (const st of state.tracks){
            nextIds.add(st.id);
            if (st.id === this.draggedItemId) continue; // don't fight an active drag
            let t = byId.get(st.id);
            if (!t) {
                t = {
                    id: st.id
                };
                this.tracks.push(t);
            }
            t.x = st.x;
            t.y = st.y;
            t.course = st.course;
            t.speed = st.speed;
            t.state = st.state;
            t.threatId = st.threatId;
            t.isHazard = st.isHazard;
            t.isUserControlled = st.isUserControlled;
            t.initialBearing = st.initialBearing;
            t.initialRange = st.initialRange;
        }
        // Remove tracks the worker no longer has (e.g. dropped from another path).
        for(let i = this.tracks.length - 1; i >= 0; i--)if (!nextIds.has(this.tracks[i].id)) this.tracks.splice(i, 1);
    }
    // Throttle drag updates to the worker (~DRAG_SYNC_MS). The local model is
    // already updated optimistically each pointermove for smooth rendering;
    // the worker only needs periodic sync to keep dependent CPA roughly current.
    // The trailing value is always flushed (on the timer or on drag release) so
    // the worker ends at the exact drop point.
    _queueDragUpdate(id, field, value) {
        this._dragPending = {
            id: id,
            field: field,
            value: value
        };
        const now = performance.now();
        if (now - (this._dragLastSent || 0) >= this.DRAG_SYNC_MS) this._flushDragUpdate();
        else if (!this._dragFlushTimer) this._dragFlushTimer = setTimeout(()=>this._flushDragUpdate(), this.DRAG_SYNC_MS);
    }
    _flushDragUpdate() {
        if (this._dragFlushTimer) {
            clearTimeout(this._dragFlushTimer);
            this._dragFlushTimer = null;
        }
        if (!this._dragPending || !this.engineClient) return;
        const { id: id, field: field, value: value } = this._dragPending;
        this.engineClient.editTrack(id, field, value);
        this._dragPending = null;
        this._dragLastSent = performance.now();
    }
    // --- Session persistence (offline resume) ---
    _buildSessionRecord() {
        const o = this.ownShip;
        return {
            version: 1,
            clock: this.simulationElapsed,
            engine: {
                ownShip: {
                    id: 'ownShip',
                    x: o.x,
                    y: o.y,
                    course: o.course,
                    speed: o.speed,
                    orderedCourse: o.orderedCourse,
                    orderedSpeed: o.orderedSpeed
                },
                tracks: this.tracks.map((t)=>({
                        id: t.id,
                        x: t.x,
                        y: t.y,
                        course: t.course,
                        speed: t.speed,
                        initialBearing: t.initialBearing,
                        initialRange: t.initialRange,
                        isHazard: !!t.isHazard,
                        isUserControlled: !!t.isUserControlled
                    }))
            },
            view: {
                selectedTrackId: this.selectedTrackId,
                simulationSpeed: this.simulationSpeed,
                isSimulationRunning: this.isSimulationRunning,
                maxRange: this.maxRange,
                vectorTimeInMinutes: this.vectorTimeInMinutes,
                showRelativeMotion: this.showRelativeMotion,
                showCPAInfo: this.showCPAInfo,
                showWeather: this.showWeather,
                showPolarPlot: this.showPolarPlot,
                showTrackIds: this.showTrackIds,
                trueWind: {
                    direction: this.trueWind.direction,
                    speed: this.trueWind.speed
                }
            }
        };
    }
    // Restore ownShip, tracks, and view settings from a saved record. Returns
    // false (so the caller starts a default scenario) if the record is missing
    // or fails validation.
    _applyRestoredSession(saved) {
        if (!saved || saved.version !== 1 || !saved.engine || !saved.engine.ownShip || !Array.isArray(saved.engine.tracks)) return false;
        const eo = saved.engine.ownShip;
        Object.assign(this.ownShip, {
            x: eo.x,
            y: eo.y,
            course: eo.course,
            speed: eo.speed,
            orderedCourse: eo.orderedCourse,
            orderedSpeed: eo.orderedSpeed
        });
        this.tracks = saved.engine.tracks.map((t)=>({
                id: t.id,
                x: t.x,
                y: t.y,
                course: t.course,
                speed: t.speed,
                initialBearing: t.initialBearing,
                initialRange: t.initialRange,
                isHazard: !!t.isHazard,
                isUserControlled: !!t.isUserControlled
            }));
        this.simulationElapsed = Number(saved.clock) || 0;
        const v = saved.view || {};
        if (typeof v.simulationSpeed === 'number') this.simulationSpeed = v.simulationSpeed;
        if (typeof v.isSimulationRunning === 'boolean') this.isSimulationRunning = v.isSimulationRunning;
        if (typeof v.maxRange === 'number') {
            this.maxRange = v.maxRange;
            const idx = this.rangeScales.indexOf(v.maxRange);
            if (idx !== -1) this.rangeIndex = idx;
        }
        if (typeof v.vectorTimeInMinutes === 'number') {
            this.vectorTimeInMinutes = v.vectorTimeInMinutes;
            const idx = this.vectorTimes.indexOf(v.vectorTimeInMinutes);
            if (idx !== -1) this.vectorTimeIndex = idx;
        }
        for (const flag of [
            'showRelativeMotion',
            'showCPAInfo',
            'showWeather',
            'showPolarPlot',
            'showTrackIds'
        ])if (typeof v[flag] === 'boolean') this[flag] = v[flag];
        if (v.trueWind && typeof v.trueWind.direction === 'number') {
            this.trueWind.direction = v.trueWind.direction;
            this.trueWind.speed = v.trueWind.speed;
        }
        // Selected track must still exist.
        if (this.tracks.some((t)=>t.id === v.selectedTrackId)) this.selectedTrackId = v.selectedTrackId;
        else this.selectedTrackId = this.tracks.length ? this.tracks[this.tracks.length - 1].id : null;
        // Re-sync UI affordances that mirror restored settings.
        if (this.rmDataContainer) this.rmDataContainer.open = this.showRelativeMotion;
        if (this.cpaDataContainer) this.cpaDataContainer.open = this.showCPAInfo;
        if (this.windDataContainer) this.windDataContainer.open = this.showWeather;
        this._updateRangeButtonClass();
        this.staticDirty = true;
        return true;
    }
    _scheduleSave() {
        if (this._destroyed || this._saveTimer) return;
        this._saveTimer = setTimeout(()=>{
            this._saveTimer = null;
            this._saveSession();
        }, this.SAVE_DEBOUNCE_MS);
    }
    _saveSessionNow() {
        if (this._saveTimer) {
            clearTimeout(this._saveTimer);
            this._saveTimer = null;
        }
        this._saveSession();
    }
    _saveSession() {
        if (this._destroyed || !this.tracks || !this.ownShip) return;
        try {
            (0, $b6828afa4dfd334d$export$ccdf34816785229e)(this._buildSessionRecord()).catch(()=>{});
        } catch (err) {
        // best-effort; ignore storage failures
        }
    }
    // --- Event Listener Setup ---
    _attachEventListeners() {
        // Persist the session when the app is backgrounded or closed so an
        // at-sea user who switches away keeps their place.
        document.addEventListener('visibilitychange', this._onVisibilityChange);
        window.addEventListener('pagehide', this._onPageHide);
        // Canvas interaction
        if (window.PointerEvent) {
            const opts = {
                passive: false
            };
            this.canvas?.addEventListener('pointerdown', this.handlePointerDown, opts);
            this.canvas?.addEventListener('pointerup', this.handlePointerUp, opts);
            this.canvas?.addEventListener('pointerleave', this.handlePointerUp, opts);
            this.canvas?.addEventListener('pointercancel', this.handlePointerUp, opts);
            this.canvas?.addEventListener('pointermove', this.handlePointerMove, opts);
            // Guarded container drag: only allow if target is not the canvas
            this.mainContainer?.addEventListener('pointerdown', (e)=>{
                if (e.target !== this.canvas) this.handleContainerPointerDown(e);
            }, opts);
            this.mainContainer?.addEventListener('pointermove', (e)=>{
                if (e.target !== this.canvas) this.handleContainerPointerMove(e);
            }, opts);
            this.mainContainer?.addEventListener('pointerup', (e)=>{
                if (e.target !== this.canvas) this.handleContainerPointerUp(e);
            }, opts);
            this.mainContainer?.addEventListener('pointercancel', (e)=>{
                if (e.target !== this.canvas) this.handleContainerPointerUp(e);
            }, opts);
        } else if ('ontouchstart' in window) {
            const wrap = (handler)=>(e)=>{
                    const touch = e.touches[0] || e.changedTouches[0];
                    if (!touch) return;
                    e.preventDefault();
                    e.stopPropagation();
                    handler({
                        clientX: touch.clientX,
                        clientY: touch.clientY,
                        button: 0,
                        buttons: 1,
                        pointerType: 'touch',
                        pointerId: touch.identifier || 0,
                        preventDefault: ()=>e.preventDefault(),
                        stopPropagation: ()=>e.stopPropagation()
                    });
                };
            const opts = {
                passive: false
            };
            this.canvas?.addEventListener('touchstart', wrap(this.handlePointerDown), opts);
            this.canvas?.addEventListener('touchmove', wrap(this.handlePointerMove), opts);
            this.canvas?.addEventListener('touchend', wrap(this.handlePointerUp));
            this.canvas?.addEventListener('touchcancel', wrap(this.handlePointerUp));
            // Guarded container drag for touch events
            this.mainContainer?.addEventListener('touchstart', (e)=>{
                if (e.target !== this.canvas) wrap(this.handleContainerPointerDown)(e);
            }, opts);
            this.mainContainer?.addEventListener('touchmove', (e)=>{
                if (e.target !== this.canvas) wrap(this.handleContainerPointerMove)(e);
            }, opts);
            this.mainContainer?.addEventListener('touchend', (e)=>{
                if (e.target !== this.canvas) wrap(this.handleContainerPointerUp)(e);
            });
            this.mainContainer?.addEventListener('touchcancel', (e)=>{
                if (e.target !== this.canvas) wrap(this.handleContainerPointerUp)(e);
            });
        } else {
            this.canvas?.addEventListener('mousedown', this.handlePointerDown);
            this.canvas?.addEventListener('mouseup', this.handlePointerUp);
            this.canvas?.addEventListener('mouseleave', this.handlePointerUp);
            this.canvas?.addEventListener('mousemove', this.handlePointerMove);
            // Guarded container drag for mouse events
            this.mainContainer?.addEventListener('mousedown', (e)=>{
                if (e.target !== this.canvas) this.handleContainerPointerDown(e);
            });
            this.mainContainer?.addEventListener('mousemove', (e)=>{
                if (e.target !== this.canvas) this.handleContainerPointerMove(e);
            });
            this.mainContainer?.addEventListener('mouseup', (e)=>{
                if (e.target !== this.canvas) this.handleContainerPointerUp(e);
            });
        }
        // Window resize
        window.addEventListener('resize', this._throttleRAF(()=>{
            this.scaleUI();
        }));
        // Control buttons
        this.btnVectorTime?.addEventListener('click', ()=>this.toggleVectorTime());
        this.btnRange?.addEventListener('click', ()=>this.toggleRange());
        this.btnPlayPause?.addEventListener('click', ()=>this.togglePlayPause());
        // Bind playback controls directly to ensure the correct handlers
        // are invoked when the buttons are pressed
        this.btnFuture?.addEventListener('click', this.fastForward.bind(this));
        this.btnPast?.addEventListener('click', this.rewind.bind(this));
        this.btnAddTrack?.addEventListener('click', ()=>this.addTrack());
        this.btnDropTrack?.addEventListener('click', ()=>this.dropTrack());
        this.btnScen?.addEventListener('click', ()=>{
            // Regenerate starts a fresh scenario and discards the saved session.
            const currentConfig = this.config;
            window.sim.destroy();
            window.sim = new $8b46766143819e9f$export$b14c74960ae6f55e({
                ...currentConfig,
                fresh: true
            });
        });
        // Help Modal
        // this.btnHelp?.addEventListener('click', () => this.showHelpModal());
        this.helpCloseBtn?.addEventListener('click', ()=>this.hideHelpModal());
        if (this.helpModal && this.helpContent && this.helpCloseBtn) new ResizeObserver(()=>{
            const scale = Math.max(0.8, Math.min(1.2, this.helpModal.clientWidth / 500));
            this.helpContent.style.fontSize = `${1 * scale}rem`;
            this.helpCloseBtn.style.fontSize = `${0.9 * scale}rem`;
            this.helpCloseBtn.style.padding = `${0.5 * scale}rem`;
        }).observe(this.helpModal);
        // Toggle Switches (Checkboxes)
        const bindToggleSwitch = (chkId, toggleFn)=>{
            const chk = document.getElementById(chkId);
            if (chk && toggleFn) chk.addEventListener('change', (e)=>{
                // Update internal state to match checkbox
                // e.target.checked is the new state
                const newState = e.target.checked;
                // We need to call toggleFn but maybe pass the state or just let toggleFn flip?
                // Expected toggleFn flips logic: this.showX = !this.showX
                // Ideally we sync force:
                toggleFn.call(this, newState);
                e.stopPropagation();
            });
        };
        // Modify toggle functions to accept optional forced state if needed, 
        // or just let them flip. Since checkboxes maintain state, we should sync.
        // But the original toggle functions just flip. 
        // Let's wrapping:
        const linkToggle = (chkId, propName, updateFn)=>{
            const chk = document.getElementById(chkId);
            if (chk) chk.addEventListener('change', (e)=>{
                this[propName] = e.target.checked;
                this.markSceneDirty();
                if (updateFn) updateFn.call(this);
                else this._scheduleUIUpdate();
            });
        };
        linkToggle('chk-toggle-rm', 'showRelativeMotion');
        linkToggle('chk-toggle-cpa', 'showCPAInfo');
        // Settings drawer interactions
        if (this.btnSettings && this.settingsDrawer) this.btnSettings.addEventListener('click', ()=>{
            this.btnSettings.classList.toggle('active');
            this.settingsDrawer.classList.toggle('active');
        });
        // Fullscreen toggle
        if (this.btnFullscreen) {
            const sim = this;
            this.btnFullscreen.addEventListener('click', function(e) {
                this.classList.toggle('active');
                sim.toggleFullScreen();
            });
        }
        document.addEventListener('keydown', (e)=>{
            if (e.key === 'Escape' && $8b46766143819e9f$var$fsApi.isActive()) $8b46766143819e9f$var$fsApi.exit();
        });
        // Settings toggles for polar plot and track IDs
        if (this.chkPolarPlot) {
            const sim = this;
            this.chkPolarPlot.addEventListener('click', function(e) {
                this.classList.toggle('active');
                sim.togglePolarPlot();
            });
        }
        if (this.chkTrackIds) {
            const sim = this;
            this.chkTrackIds.addEventListener('click', function(e) {
                this.classList.toggle('active');
                sim.toggleTrackIds();
            });
        }
        // Shared tooltip behavior for elements with data-tooltip
        document.querySelectorAll('[data-tooltip]').forEach((el)=>{
            el.addEventListener('pointerenter', (e)=>{
                this.dragTooltip.textContent = el.getAttribute('data-tooltip');
                this.dragTooltip.style.color = this.radarGreen;
                this.dragTooltip.style.display = 'block';
                // Dynamic positioning logic (buffer, edge-aware)
                const buffer = 10;
                const tooltipWidth = this.dragTooltip.offsetWidth;
                const tooltipHeight = this.dragTooltip.offsetHeight;
                let x = e.clientX + buffer;
                if (e.clientX + tooltipWidth + buffer > window.innerWidth) x = e.clientX - tooltipWidth - buffer;
                const y = e.clientY - tooltipHeight - buffer;
                this.dragTooltip.style.transform = `translate(${x}px, ${y}px)`;
            });
            el.addEventListener('pointermove', (e)=>{
                if (this.dragTooltip.style.display === 'block') {
                    // Dynamic positioning logic (buffer, edge-aware)
                    const buffer = 10;
                    const tooltipWidth = this.dragTooltip.offsetWidth;
                    const tooltipHeight = this.dragTooltip.offsetHeight;
                    let x = e.clientX + buffer;
                    if (e.clientX + tooltipWidth + buffer > window.innerWidth) x = e.clientX - tooltipWidth - buffer;
                    const y = e.clientY - tooltipHeight - buffer;
                    this.dragTooltip.style.transform = `translate(${x}px, ${y}px)`;
                }
            });
            el.addEventListener('pointerleave', ()=>{
                this.dragTooltip.style.display = 'none';
            });
            el.addEventListener('pointerdown', ()=>{
                this.dragTooltip.style.display = 'none';
            });
        });
        [
            'fullscreenchange',
            'webkitfullscreenchange',
            'mozfullscreenchange',
            'MSFullscreenChange'
        ].forEach((evt)=>document.addEventListener(evt, ()=>this._syncFullscreenUI()));
        this._syncFullscreenUI();
        // Editable fields
        this.dataPane?.addEventListener('click', (e)=>{
            if (e.target.classList.contains('editable')) {
                if (this.activeEditField) return; // Prevent new input if one is already active
                this.activeEditField = e.target.id;
                this._scheduleUIUpdate();
            }
        });
    }
    // Clears all simulation state and graphics buffers for garbage collection
    destroy() {
        this._saveSessionNow();
        this._destroyed = true;
        this.gameLoop.running = false;
        cancelAnimationFrame(this._raf);
        clearInterval(this._myInterval);
        clearTimeout(this._myTimeout);
        clearTimeout(this._dragFlushTimer);
        clearTimeout(this._saveTimer);
        this._clearLongPress();
        this._ghostContact = null;
        this._activePointers.clear();
        this._pinching = false;
        this._pinchPrev = null;
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        window.removeEventListener('pagehide', this._onPageHide);
        if (this.engineClient) {
            this.engineClient.destroy();
            this.engineClient = null;
        }
        window.removeEventListener('resize', this._onResize);
        if (this.tracks) this.tracks.length = 0;
        this.ownShip = null;
        this.tracks = null;
        this.targets = null;
        // wipe canvas to release GPU memory
        if (this.canvas) {
            this.canvas.width = this.canvas.width;
            this.canvas.height = this.canvas.height;
        }
        this.ctx = null;
    }
    // --- Vector Time Toggle ---
    toggleVectorTime() {
        this.vectorTimeIndex = (this.vectorTimeIndex + 1) % this.vectorTimes.length;
        this.vectorTimeInMinutes = this.vectorTimes[this.vectorTimeIndex];
        this.btnVectorTime.textContent = `${this.vectorTimeInMinutes} min`;
        this.markSceneDirty();
    }
    /**
     * Toggle play/pause of the simulation.
     */ togglePlayPause() {
        // If FF/RW active, reset to normal play and resume play immediately
        if (this.simulationSpeed !== 1) {
            // Stop fast-forward/rewind and resume normal play immediately
            this.simulationSpeed = 1;
            this.updateSpeedIndicator();
            this.updateButtonStyles();
            this.isSimulationRunning = true;
            this.btnPlayPause.classList.remove('pause');
            this.startGameLoop();
            return;
        }
        // Toggle play/pause
        if (this.isSimulationRunning) {
            this.isSimulationRunning = false;
            this.btnPlayPause.classList.add('pause');
        } else {
            this.isSimulationRunning = true;
            this.btnPlayPause.classList.remove('pause');
            this.startGameLoop();
        }
        this.updateSpeedIndicator();
        this.updateButtonStyles();
    }
    /**
     * Cycle forward through fast-forward speeds.
     */ fastForward() {
        // Cycle through 25×/50× or reset to normal (1×)
        if (this.simulationSpeed === this.ffSpeeds[this.ffSpeeds.length - 1]) this.simulationSpeed = 1;
        else {
            if (!this.isSimulationRunning) this.isSimulationRunning = true;
            const idx = this.ffSpeeds.indexOf(this.simulationSpeed);
            this.simulationSpeed = idx === -1 ? this.ffSpeeds[0] : this.ffSpeeds[idx + 1] || 1;
        }
        this.updateSpeedIndicator();
        this.updateButtonStyles();
        this.markSceneDirty();
        this.btnPlayPause.classList.remove('pause');
    }
    /**
     * Cycle backward through rewind speeds.
     */ rewind() {
        // Cycle through –25×/–50× or reset to normal (1×)
        if (this.simulationSpeed === this.revSpeeds[this.revSpeeds.length - 1]) this.simulationSpeed = 1;
        else {
            if (!this.isSimulationRunning) this.isSimulationRunning = true;
            const idx = this.revSpeeds.indexOf(this.simulationSpeed);
            this.simulationSpeed = idx === -1 ? this.revSpeeds[0] : this.revSpeeds[idx + 1] || 1;
        }
        this.updateSpeedIndicator();
        this.updateButtonStyles();
        this.markSceneDirty();
        this.btnPlayPause.classList.remove('pause');
    }
    // TODO: Optimize the simulation loop to handle ~50-100 tracks smoothly.
    // - Avoid creating new objects or strings every frame (reuse them instead).
    // - Do minimal work for off-screen or non-selected tracks.
    // - Keep each frame under ~16ms.
    // --- Animation & Optimizations ---
    gameLoop(timestamp) {
        if (this._destroyed || !this.tracks) {
            this.gameLoop.running = false;
            return;
        }
        this.lastTimestamp = timestamp;
        // Physics now happens in the worker. Each frame we fold in the most
        // recent authoritative snapshot, then recompute derived data + render.
        if (this._pendingSnapshot) {
            this._applySnapshot(this._pendingSnapshot);
            this._pendingSnapshot = null;
            this.sceneDirty = true;
        }
        if (this.sceneDirty) {
            this.tracks.forEach((t)=>this.calculateAllData(t));
            this.calculateWindData();
            this.drawRadar();
            this.sceneDirty = false;
        }
        if (timestamp - this.lastDomUpdate >= this.DOM_UPDATE_INTERVAL) {
            this.updateOwnShipPanel();
            this.updateDataPanels();
            this.updateButtonStyles();
            this.updateSimClock();
            this.lastDomUpdate = timestamp;
        }
        if (this.isSimulationRunning || this.sceneDirty || this._pendingSnapshot) requestAnimationFrame(this.gameLoop);
        else this.gameLoop.running = false;
    }
    startGameLoop() {
        if (!this.gameLoop.running) {
            this.gameLoop.running = true;
            requestAnimationFrame(this.gameLoop);
        }
    }
    markSceneDirty() {
        this.sceneDirty = true;
        this.startGameLoop();
        this._scheduleSave();
    }
    _throttleRAF(fn) {
        let running = false;
        return (...args)=>{
            if (!running) {
                running = true;
                requestAnimationFrame(()=>{
                    fn.apply(this, args);
                    running = false;
                });
            }
        };
    }
    _setText(id, value) {
        const el = document.getElementById(id);
        if (el && el.textContent !== value) el.textContent = value;
    }
    _renderEditableField(id, displayValue, numericValue) {
        const el = document.getElementById(id);
        if (!el) return;
        if (this.activeEditField === id) {
            if (this.suppressEditRender) return;
            if (!el.querySelector('input')) {
                el.innerHTML = `<input type="text" value="${parseFloat(numericValue).toFixed(1)}">`;
                const input = el.querySelector('input');
                const commit = ()=>{
                    const newVal = parseFloat(input.value);
                    this.activeEditField = null;
                    this.suppressEditRender = true; // block edit‑field render for the next frame
                    el.textContent = this._formatDisplayValue(id, newVal); // immediate static text
                    this.commitEdit(id, newVal);
                    this._scheduleUIUpdate(); // refresh, block lifted inside
                };
                input.addEventListener('blur', commit, {
                    once: true
                });
                input.addEventListener('keydown', (e)=>{
                    if (e.key === 'Enter') input.blur();
                    else if (e.key === 'Escape') {
                        this.activeEditField = null;
                        this._scheduleUIUpdate();
                    }
                });
                setTimeout(()=>{
                    input.focus();
                    input.select();
                }, 0);
            }
        } else if (el.textContent !== displayValue) el.textContent = displayValue;
    }
    /**
     * Format the numeric value for immediate static display after editing.
     */ _formatDisplayValue(id, num) {
        if (id === 'ownship-crs' || id === 'track-crs' || id === 'track-brg') return `${this.formatBearing(num)} T`;
        if (id === 'ownship-spd' || id === 'track-spd') return `${num.toFixed(1)} KTS`;
        if (id === 'track-rng') return `${num.toFixed(1)} NM`;
        return String(num);
    }
    /**
     * Schedule a single UI refresh on the next animation frame.
     * Prevents rapid back‑to‑back redraws that cause visual bounce.
     * Also clears the temporary suppress flag used during input‑blur commits.
     */ _scheduleUIUpdate() {
        if (this.uiUpdatePending) return;
        this.uiUpdatePending = true;
        requestAnimationFrame(()=>{
            this.uiUpdatePending = false;
            this.suppressEditRender = false; // lift one‑frame block
            this.updatePanelsAndRedraw();
        });
    }
    commitEdit(id, value) {
        let didUpdate = false;
        if (isNaN(value)) {
            this.activeEditField = null;
            this._scheduleUIUpdate();
            return;
        }
        const track = this.tracks.find((t)=>t.id === this.selectedTrackId);
        // Mutations are applied optimistically to the local model for instant
        // feedback and mirrored to the worker, which is authoritative.
        if (id === 'ownship-crs') {
            this.ownShip.orderedCourse = Math.max(0, Math.min(359.9, value));
            this.engineClient.editOwnShip('orderedCourse', this.ownShip.orderedCourse);
            didUpdate = true;
        } else if (id === 'ownship-spd') {
            this.ownShip.orderedSpeed = value;
            this.engineClient.editOwnShip('orderedSpeed', this.ownShip.orderedSpeed);
            didUpdate = true;
        } else if (track) {
            if (id === 'track-brg') {
                track.bearing = Math.max(0, Math.min(359.9, value));
                didUpdate = true;
            } else if (id === 'track-rng') {
                track.range = Math.max(0, Math.min(359.9, value));
                didUpdate = true;
            } else if (id === 'track-crs') {
                track.course = Math.max(0, Math.min(359.9, value));
                this.engineClient.editTrack(track.id, 'course', track.course);
                didUpdate = true;
            } else if (id === 'track-spd') {
                track.speed = value;
                this.engineClient.editTrack(track.id, 'speed', track.speed);
                didUpdate = true;
            }
            if ((id === 'track-brg' || id === 'track-rng') && track.bearing !== undefined && track.range !== undefined) {
                const angleRad = this.toRadians(track.bearing);
                track.x = this.ownShip.x + track.range * Math.sin(angleRad);
                track.y = this.ownShip.y + track.range * Math.cos(angleRad);
                this.engineClient.editTrack(track.id, 'position', {
                    x: track.x,
                    y: track.y
                });
            }
        }
        this.activeEditField = null;
        this._scheduleUIUpdate();
        if (didUpdate) this.markSceneDirty();
    }
    // --- Calculations ---
    calculateAllData(track) {
        const dx = track.x - this.ownShip.x;
        const dy = track.y - this.ownShip.y;
        track.range = Math.max(0, Math.min(359.9, Math.sqrt(dx ** 2 + dy ** 2)));
        track.bearing = (this.toDegrees(Math.atan2(dx, dy)) + 360) % 360;
        const ownShipCanvasAngle = this.toRadians(this.bearingToCanvasAngle(this.ownShip.course));
        const ownShipVelX = this.ownShip.speed * Math.cos(ownShipCanvasAngle);
        const ownShipVelY = this.ownShip.speed * Math.sin(ownShipCanvasAngle);
        const targetCourseCanvasAngle = this.toRadians(this.bearingToCanvasAngle(track.course));
        const targetVelX = track.speed * Math.cos(targetCourseCanvasAngle);
        const targetVelY = track.speed * Math.sin(targetCourseCanvasAngle);
        const relVelX = targetVelX - ownShipVelX;
        const relVelY = targetVelY - ownShipVelY;
        const relSpeed = Math.sqrt(relVelX ** 2 + relVelY ** 2);
        const relVectorCanvasAngle = this.toDegrees(Math.atan2(relVelY, relVelX));
        if (!track.rmVector) track.rmVector = {
            x: 0,
            y: 0,
            speed: 0,
            bearing: 0
        };
        track.rmVector.x = relVelX;
        track.rmVector.y = relVelY;
        track.rmVector.speed = relSpeed;
        track.rmVector.bearing = this.canvasAngleToBearing(relVectorCanvasAngle);
        const targetPosCanvasAngle = this.toRadians(this.bearingToCanvasAngle(track.bearing));
        const targetPosX = track.range * Math.cos(targetPosCanvasAngle);
        const targetPosY = track.range * Math.sin(targetPosCanvasAngle);
        const dotProduct = targetPosX * relVelX + targetPosY * relVelY;
        if (!track.cpa) track.cpa = {
            range: '--',
            time: '--:--:--',
            brg: '--'
        };
        if (relSpeed < 0.001) {
            track.cpa.range = '--';
            track.cpa.time = '--:--:--';
            track.cpa.brg = '--';
            track.hasPassedCPA = true;
        } else {
            const tcpa = -dotProduct / relSpeed ** 2;
            track.hasPassedCPA = tcpa < 0;
            const cpaX = targetPosX + tcpa * relVelX;
            const cpaY = targetPosY + tcpa * relVelY;
            if (!track.cpaPosition) track.cpaPosition = {
                x: 0,
                y: 0
            };
            track.cpaPosition.x = cpaX;
            track.cpaPosition.y = cpaY;
            if (track.hasPassedCPA) {
                track.cpa.range = '-- nm';
                track.cpa.time = '--:--:--';
                track.cpa.brg = '--';
            } else {
                const cpaRange = Math.sqrt(cpaX ** 2 + cpaY ** 2);
                const cpaCanvasAngle = this.toDegrees(Math.atan2(cpaY, cpaX));
                const cpaBearing = this.canvasAngleToBearing(cpaCanvasAngle);
                const cpaQuarter = this.getRelativeQuarter(cpaBearing, this.ownShip.course);
                track.cpa.range = `${cpaRange.toFixed(1)} nm`;
                track.cpa.time = this.formatTime(tcpa);
                track.cpa.brg = `${this.formatBearing(cpaBearing)} T / ${cpaQuarter}`;
            }
        }
        const ownshipBearingFromTarget = (track.bearing + 180) % 360;
        const targetAngle = (ownshipBearingFromTarget - track.course + 360) % 360;
        if (!track.rm) track.rm = {
            dir: '',
            spd: '',
            rate: '',
            angle: '',
            aspect: ''
        };
        // TODO: Only format bearing/speed strings for the selected track or when updating the UI, not for every track each frame.
        track.rm.dir = `${this.formatBearing(track.rmVector.bearing)} T`;
        track.rm.spd = `${relSpeed.toFixed(1)} kts`;
        track.rm.rate = this.getBearingRate({
            x: relVelX,
            y: relVelY
        }, {
            x: targetPosX,
            y: targetPosY
        }, track.range);
        track.rm.angle = `${this.formatBearing(targetAngle)} deg`;
    // track.rm.aspect = this.getAspect(targetAngle);
    }
    calculateWindData() {
        const trueWindVectorAngle = (this.trueWind.direction + 180) % 360;
        const trueWindRad = this.toRadians(this.bearingToCanvasAngle(trueWindVectorAngle));
        const trueWindVelX = this.trueWind.speed * Math.cos(trueWindRad);
        const trueWindVelY = this.trueWind.speed * Math.sin(trueWindRad);
        const ownShipRad = this.toRadians(this.bearingToCanvasAngle(this.ownShip.course));
        const ownShipVelX = this.ownShip.speed * Math.cos(ownShipRad);
        const ownShipVelY = this.ownShip.speed * Math.sin(ownShipRad);
        const relWindVelX = trueWindVelX - ownShipVelX;
        const relWindVelY = trueWindVelY - ownShipVelY;
        this.relativeWind.speed = Math.sqrt(relWindVelX ** 2 + relWindVelY ** 2);
        const relWindVectorCanvasAngle = this.toDegrees(Math.atan2(relWindVelY, relWindVelX));
        this.relativeWind.vectorDirection = this.canvasAngleToBearing(relWindVectorCanvasAngle);
    }
    // --- Drawing ---
    // Tokens are authored in CSS px; scale to device pixels since the canvas
    // is sized by DPR and applies no context transform.
    strokeWidth(token) {
        return (0, $759706aefe3a0ba6$export$ca4d951bcf1370f)[token] * this.DPR;
    }
    drawRadar() {
        // Debug: log frame and fill red overlay
        // console.log('drawRadar called:', { width: this.canvas.width, height: this.canvas.height });
        // Debug fill to confirm drawing
        this.ctx.save();
        this.ctx.fillStyle = 'rgba(255, 0, 0, 0.1)';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.restore();
        const size = this.canvas.width;
        if (size === 0) return;
        const center = size / 2;
        const radius = size / 2 * 0.9;
        if (this.staticDirty || this.staticCanvas.width !== size) {
            this.staticCanvas.width = size;
            this.staticCanvas.height = size;
            this.drawStaticRadar();
            this.staticDirty = false;
        }
        // Inspection magnifier: scale/translate the whole scene around the pinch
        // focus. Vectors/symbols/labels re-rasterize crisply; ownship stays the
        // data center (this is a view camera, not a range change).
        this.ctx.save();
        this.ctx.translate(this.viewOffset.x, this.viewOffset.y);
        this.ctx.scale(this.viewScale, this.viewScale);
        this.ctx.drawImage(this.staticCanvas, 0, 0); // layers 1-3: background, range rings, bearing ticks / static geometry
        // Layer 4: auxiliary / projection lines (dotted bearing guide line).
        if (this.selectedTrackId !== null) {
            const selectedTrack = this.tracks.find((t)=>t.id === this.selectedTrackId);
            if (selectedTrack) this.drawBearingLine(center, radius, selectedTrack);
        }
        // Layer 5: CPA / analytical overlays.
        if (this.showCPAInfo && this.selectedTrackId !== null) this.drawCPAIndicator(center, radius);
        // Environmental wind vectors sit beneath the target layers.
        if (this.showWeather) this.drawWeatherInfo(center, radius);
        // Layer 6: target vectors (course/speed + relative motion), beneath symbols.
        this.tracks.forEach((track)=>{
            if (track.range > this.maxRange) return;
            this.drawTargetVector(center, radius, track);
            if (this.showRelativeMotion) this.drawRelativeMotionVector(center, radius, track);
        });
        // Layer 7: normal target symbols. Selected/alert symbols are lifted to layers 9-10.
        this.tracks.forEach((track)=>{
            if (track.range > this.maxRange) return;
            if (track.id === this.selectedTrackId || this.isAlertTarget(track)) return;
            this.drawTargetSymbol(center, radius, track);
        });
        // Layer 8: ownship symbol + heading vector (above normal targets).
        this.drawOwnShipIcon(center, radius);
        // Layer 9: selected target styling — symbol lifted above normal targets, plus selection ring.
        if (this.selectedTrackId !== null) {
            const sel = this.tracks.find((t)=>t.id === this.selectedTrackId);
            if (sel && sel.range <= this.maxRange && !this.isAlertTarget(sel)) this.drawTargetSymbol(center, radius, sel);
            this.drawSelectionIndicator(center, radius, this.selectedTrackId, this.radarWhite, this.strokeWidth('focus'), (0, $759706aefe3a0ba6$export$65f5e322c6eb47c1));
        }
        if (this.hoveredTrackId !== null && this.hoveredTrackId !== this.selectedTrackId) this.drawSelectionIndicator(center, radius, this.hoveredTrackId, this.radarFaintWhite, this.strokeWidth('auxiliary'));
        // Layer 10: alert / warning styling — alert symbols topmost, plus caution glyph.
        this.tracks.forEach((track)=>{
            if (track.range > this.maxRange) return;
            if (!this.isAlertTarget(track)) return;
            this.drawTargetSymbol(center, radius, track);
            this.drawAlertGlyph(center, radius, track);
        });
        // Layer 11: target labels, drawn last so they stay legible above all marks.
        this.tracks.forEach((track)=>{
            if (track.range > this.maxRange) return;
            this.drawTargetLabel(center, radius, track);
        });
        // Layer 12: ghost contact being placed via long-press (topmost preview).
        this._drawGhost(center, radius);
        this.ctx.restore();
    }
    getScreenPos(wx, wy, center, radius) {
        const dx = wx - this.ownShip.x;
        const dy = wy - this.ownShip.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        // Optimize: if distance is huge, maybe don't calculate? But canvas handles clipping.
        const bearing = (this.toDegrees(Math.atan2(dx, dy)) + 360) % 360;
        const angleRad = this.toRadians(this.bearingToCanvasAngle(bearing));
        const distOnCanvas = dist / this.maxRange * radius;
        return {
            x: center + distOnCanvas * Math.cos(angleRad),
            y: center - distOnCanvas * Math.sin(angleRad)
        };
    }
    drawStaticRadar() {
        const size = this.staticCanvas.width;
        if (size === 0) return;
        const center = size / 2;
        const radius = size / 2 * 0.9;
        const ctx = this.staticCtx;
        ctx.save();
        // --- Outer and inner range rings ---
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, size, size);
        ctx.strokeStyle = this.radarFaintGreen;
        ctx.lineWidth = this.strokeWidth('reference');
        ctx.globalAlpha = (0, $759706aefe3a0ba6$export$9541d2d7326be049);
        ctx.beginPath();
        ctx.arc(center, center, radius, 0, 2 * Math.PI);
        ctx.stroke();
        for(let i = 1; i < 3; i++){
            ctx.beginPath();
            ctx.arc(center, center, radius * (i / 3), 0, 2 * Math.PI);
            ctx.stroke();
        }
        ctx.globalAlpha = 1;
        // --- Range ring labels ---
        ctx.fillStyle = this.radarFaintGreen;
        ctx.font = `bold ${Math.max(11, radius * 0.038)}px 'IBM Plex Sans Mono', monospace`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        for(let i = 1; i <= 3; i++){
            const ringRadius = radius * (i / 3);
            const range = this.maxRange * (i / 3);
            ctx.fillText(range.toFixed(1), center + ringRadius + $8b46766143819e9f$var$LABEL_OFFSET_PX, center);
        }
        // --- Radial bearing lines ---
        if (this.showPolarPlot) {
            ctx.globalAlpha = (0, $759706aefe3a0ba6$export$9541d2d7326be049);
            for(let deg = 0; deg < 360; deg += 10){
                const isCardinal = $8b46766143819e9f$var$CARDINAL_BEARINGS.includes(deg);
                ctx.setLineDash(isCardinal ? (0, $759706aefe3a0ba6$export$50ae0dfaa401c539).solid : (0, $759706aefe3a0ba6$export$50ae0dfaa401c539).bearingTick);
                ctx.lineWidth = this.strokeWidth(isCardinal ? 'reference' : 'auxiliary');
                const ang = this.toRadians(deg);
                const originalRadius = isCardinal ? size / 2 : radius + (size / 2 - radius) / 2;
                const startRadius = radius;
                let endRadius = originalRadius;
                if (!isCardinal) endRadius = radius + 0.8 * (originalRadius - radius);
                ctx.beginPath();
                ctx.moveTo(center + startRadius * Math.cos(ang), center - startRadius * Math.sin(ang));
                ctx.lineTo(center + endRadius * Math.cos(ang), center - endRadius * Math.sin(ang));
                ctx.stroke();
            }
        }
        ctx.restore();
    }
    drawRangeRings(center, radius) {
        this.ctx.strokeStyle = this.radarFaintGreen;
        this.ctx.lineWidth = this.strokeWidth('reference');
        this.ctx.beginPath();
        this.ctx.arc(center, center, radius, 0, 2 * Math.PI);
        this.ctx.stroke();
        for(let i = 1; i < 3; i++){
            this.ctx.beginPath();
            this.ctx.arc(center, center, radius * (i / 3), 0, 2 * Math.PI);
            this.ctx.stroke();
        }
    }
    drawRangeLabels(center, radius) {
        this.ctx.fillStyle = this.radarFaintGreen;
        this.ctx.font = `bold ${Math.max(11, radius * 0.038)}px 'IBM Plex Sans Mono',monospace`;
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'middle';
        for(let i = 1; i <= 3; i++){
            const ringRadius = radius * (i / 3);
            const range = this.maxRange * (i / 3);
            this.ctx.fillText(range.toFixed(1), center + ringRadius + $8b46766143819e9f$var$LABEL_OFFSET_PX, center);
        }
    }
    drawOwnShipIcon(center, radius) {
        this.ctx.strokeStyle = this.radarGreen;
        this.ctx.lineWidth = this.strokeWidth('focus');
        const iconRadius = this.canvas.width * 0.014;
        this.ctx.beginPath();
        this.ctx.arc(center, center, iconRadius, 0, 2 * Math.PI);
        this.ctx.stroke();
        const timeInHours = this.vectorTimeInMinutes / 60;
        const pixelsPerNm = radius / this.maxRange;
        const vectorDistPixels = this.ownShip.speed * timeInHours * pixelsPerNm;
        const courseAngle = this.toRadians(this.bearingToCanvasAngle(this.ownShip.course));
        const endX = center + vectorDistPixels * Math.cos(courseAngle);
        const endY = center - vectorDistPixels * Math.sin(courseAngle);
        this.ownShip.vectorEndpoint = {
            x: endX,
            y: endY
        };
        // Draw ordered course/speed vector if still manoeuvring
        const orderedCourse = this.ownShip.orderedCourse;
        const orderedSpeed = this.ownShip.orderedSpeed;
        const diffCourse = Math.abs((orderedCourse - this.ownShip.course + 540) % 360 - 180);
        const diffSpeed = Math.abs(orderedSpeed - this.ownShip.speed);
        if (diffCourse > 0.5 || diffSpeed > 0.05) {
            const orderDistPixels = orderedSpeed * timeInHours * pixelsPerNm;
            const orderAngle = this.toRadians(this.bearingToCanvasAngle(orderedCourse));
            const oEndX = center + orderDistPixels * Math.cos(orderAngle);
            const oEndY = center - orderDistPixels * Math.sin(orderAngle);
            this.ownShip.orderedVectorEndpoint = {
                x: oEndX,
                y: oEndY
            };
            this.ctx.save();
            this.ctx.strokeStyle = this.radarDarkOrange;
            this.ctx.lineWidth = this.strokeWidth('focus');
            this.ctx.beginPath();
            this.ctx.moveTo(center, center);
            this.ctx.lineTo(oEndX, oEndY);
            this.ctx.stroke();
            this.ctx.restore();
            const rect = this.canvas.getBoundingClientRect();
            const tipX = rect.left + oEndX / this.DPR;
            const tipY = rect.top + oEndY / this.DPR;
            const txt = `Crs: ${this.formatBearing(orderedCourse)} T\nSpd: ${orderedSpeed.toFixed(1)} kts`;
            this.orderTooltip.style.color = this.radarDarkOrange;
            this.orderTooltip.innerText = txt;
            this.orderTooltip.style.display = 'block';
            this.orderTooltip.style.transform = `translate(${tipX - this.orderTooltip.offsetWidth - 10}px, ${tipY - this.orderTooltip.offsetHeight - 10}px)`;
        } else {
            this.orderTooltip.style.display = 'none';
            this.ownShip.orderedVectorEndpoint = null;
        }
        const dragging = this.draggedItemId === 'ownShip' && this.dragType === 'vector';
        if (dragging && this.ownShip.dragCourse !== null && this.ownShip.dragSpeed !== null) {
            const dragDistPixels = this.ownShip.dragSpeed * timeInHours * pixelsPerNm;
            const dragAngle = this.toRadians(this.bearingToCanvasAngle(this.ownShip.dragCourse));
            const dEndX = center + dragDistPixels * Math.cos(dragAngle);
            const dEndY = center - dragDistPixels * Math.sin(dragAngle);
            this.ctx.save();
            this.ctx.strokeStyle = this.radarWhite;
            this.ctx.lineWidth = this.strokeWidth('focus');
            this.ctx.beginPath();
            this.ctx.moveTo(center, center);
            this.ctx.lineTo(dEndX, dEndY);
            this.ctx.stroke();
            this.ctx.restore();
        }
        this.ctx.strokeStyle = this.radarGreen;
        this.ctx.lineWidth = this.strokeWidth('focus');
        this.ctx.beginPath();
        this.ctx.moveTo(center, center);
        this.ctx.lineTo(endX, endY);
        this.ctx.stroke();
    }
    getTargetCoords(center, radius, track) {
        const angleRad = this.toRadians(this.bearingToCanvasAngle(track.bearing));
        const distOnCanvas = track.range / this.maxRange * radius;
        const x = center + distOnCanvas * Math.cos(angleRad);
        const y = center - distOnCanvas * Math.sin(angleRad);
        return {
            x: x,
            y: y
        };
    }
    // Alert/warning state hook: a target the engine has already flagged as a
    // collision threat (threatId, derived from existing CPA thresholds). Single
    // source of truth so styling stays decoupled from the risk criteria.
    isAlertTarget(track) {
        return !!(track && track.threatId);
    }
    // Layer 6: a target's course/speed vector. Also records vectorEndpoint (used for
    // vector-drag hit-testing), so it must run each frame for in-range tracks.
    drawTargetVector(center, radius, track) {
        const { x: x, y: y } = this.getTargetCoords(center, radius, track);
        const isSelected = track.id === this.selectedTrackId;
        const isAlert = this.isAlertTarget(track);
        // Non-selected vectors recede to object weight + reduced opacity to cut clutter;
        // selected and alert targets keep a full-strength vector so their motion stays clear.
        this.ctx.strokeStyle = this.radarGreen;
        this.ctx.lineWidth = this.strokeWidth(isSelected ? 'focus' : 'object');
        this.ctx.globalAlpha = isSelected || isAlert ? 1 : (0, $759706aefe3a0ba6$export$34cfc868cef4d0b5);
        const timeInHours = this.vectorTimeInMinutes / 60;
        const pixelsPerNm = radius / this.maxRange;
        const vectorDistPixels = track.speed * timeInHours * pixelsPerNm;
        const courseAngle = this.toRadians(this.bearingToCanvasAngle(track.course));
        const endX = x + vectorDistPixels * Math.cos(courseAngle);
        const endY = y - vectorDistPixels * Math.sin(courseAngle);
        track.vectorEndpoint = {
            x: endX,
            y: endY
        };
        this.ctx.beginPath();
        this.ctx.moveTo(x, y);
        this.ctx.lineTo(endX, endY);
        this.ctx.stroke();
        this.ctx.globalAlpha = 1;
    }
    // Layers 7/9/10: a target's symbol (box). Outline weight encodes priority:
    // alert (warning) > focus (selected) > object (normal).
    drawTargetSymbol(center, radius, track) {
        const { x: x, y: y } = this.getTargetCoords(center, radius, track);
        const targetSize = Math.max(11, radius * 0.038);
        const isSelected = track.id === this.selectedTrackId;
        const isAlert = this.isAlertTarget(track);
        this.ctx.strokeStyle = this.radarGreen;
        this.ctx.lineWidth = this.strokeWidth(isAlert ? 'alert' : isSelected ? 'focus' : 'object');
        this.ctx.strokeRect(x - targetSize / 2, y - targetSize / 2, targetSize, targetSize);
    }
    // Layer 10: alert caution glyph — the second, non-width alert channel, always
    // shown for warning targets so alert state never relies on stroke width alone.
    drawAlertGlyph(center, radius, track) {
        const { x: x, y: y } = this.getTargetCoords(center, radius, track);
        const targetSize = Math.max(11, radius * 0.038);
        this.ctx.save();
        this.ctx.fillStyle = this.radarGreen;
        this.ctx.font = `bold ${Math.max(14, radius * 0.052)}px 'IBM Plex Sans Mono', monospace`;
        this.ctx.textAlign = 'right';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText((0, $759706aefe3a0ba6$export$1cb94782fae13269), x - targetSize / 2 - 4, y);
        this.ctx.restore();
    }
    // Layer 11: a target's id label.
    drawTargetLabel(center, radius, track) {
        if (!this.showTrackIds) return;
        const { x: x, y: y } = this.getTargetCoords(center, radius, track);
        const targetSize = Math.max(11, radius * 0.038);
        this.ctx.fillStyle = this.radarGreen;
        this.ctx.font = `${Math.max(11, radius * 0.038)}px 'IBM Plex Sans Mono', monospace`;
        this.ctx.textAlign = 'left';
        this.ctx.textBaseline = 'top';
        this.ctx.fillText(track.id, x + targetSize / 2 + 3, y + targetSize / 2 + 3);
    }
    drawRelativeMotionVector(center, radius, track) {
        if (!track.rmVector) return;
        const { x: startX, y: startY } = this.getTargetCoords(center, radius, track);
        const timeInHours = this.vectorTimeInMinutes / 60;
        const pixelsPerNm = radius / this.maxRange;
        const vectorDistPixels = track.rmVector.speed * timeInHours * pixelsPerNm;
        const vectorAngleRad = this.toRadians(this.bearingToCanvasAngle(track.rmVector.bearing));
        const endX = startX + vectorDistPixels * Math.cos(vectorAngleRad);
        const endY = startY - vectorDistPixels * Math.sin(vectorAngleRad);
        this.ctx.save();
        this.ctx.strokeStyle = this.radarGreen;
        this.ctx.lineWidth = this.strokeWidth('vector');
        this.ctx.setLineDash((0, $759706aefe3a0ba6$export$50ae0dfaa401c539).relativeMotion);
        this.ctx.beginPath();
        this.ctx.moveTo(startX, startY);
        this.ctx.lineTo(endX, endY);
        this.ctx.stroke();
        this.ctx.restore();
    }
    drawCPAIndicator(center, radius) {
        const track = this.tracks.find((t)=>t.id === this.selectedTrackId);
        if (!track || track.hasPassedCPA || !track.cpaPosition) return;
        const pixelsPerNm = radius / this.maxRange;
        const cpaBearing = (this.toDegrees(Math.atan2(track.cpaPosition.x, track.cpaPosition.y)) + 360) % 360;
        const cpaCanvasAngle = this.toRadians(this.bearingToCanvasAngle(cpaBearing));
        const cpaRange = Math.sqrt(track.cpaPosition.x ** 2 + track.cpaPosition.y ** 2);
        const cpaDistCanvas = cpaRange * pixelsPerNm;
        const cpaX = center + cpaDistCanvas * Math.cos(cpaCanvasAngle);
        const cpaY = center - cpaDistCanvas * Math.sin(cpaCanvasAngle);
        this.ctx.beginPath();
        this.ctx.arc(cpaX, cpaY, 4, 0, 2 * Math.PI);
        this.ctx.fillStyle = this.radarGreen;
        this.ctx.fill();
        this.ctx.save();
        // Selected-target CPA line: analytical overlay — above rings/projections, below vectors.
        this.ctx.strokeStyle = this.radarFaintGreen;
        this.ctx.lineWidth = this.strokeWidth('overlay');
        this.ctx.setLineDash((0, $759706aefe3a0ba6$export$50ae0dfaa401c539).cpaLine);
        this.ctx.beginPath();
        this.ctx.moveTo(center, center);
        this.ctx.lineTo(cpaX, cpaY);
        this.ctx.stroke();
        this.ctx.restore();
    }
    drawBearingLine(center, radius, track) {
        const { x: x, y: y } = this.getTargetCoords(center, radius, track);
        this.ctx.save();
        this.ctx.strokeStyle = this.radarWhite;
        this.ctx.lineWidth = this.strokeWidth('auxiliary');
        this.ctx.globalAlpha = (0, $759706aefe3a0ba6$export$cab7f44c7a8c34dc);
        this.ctx.setLineDash((0, $759706aefe3a0ba6$export$50ae0dfaa401c539).bearingLine);
        this.ctx.beginPath();
        this.ctx.moveTo(center, center);
        this.ctx.lineTo(x, y);
        this.ctx.stroke();
        this.ctx.restore();
    }
    drawSelectionIndicator(center, radius, trackId, color, lineWidth, radiusScale = (0, $759706aefe3a0ba6$export$718af19761f21695)) {
        const track = this.tracks.find((t)=>t.id === trackId);
        if (!track) return;
        const { x: x, y: y } = this.getTargetCoords(center, radius, track);
        const targetSize = Math.max(11, radius * 0.038);
        const indicatorRadius = targetSize * radiusScale;
        this.ctx.strokeStyle = color;
        this.ctx.lineWidth = lineWidth;
        this.ctx.beginPath();
        this.ctx.arc(x, y, indicatorRadius, 0, 2 * Math.PI);
        this.ctx.stroke();
    }
    drawWeatherInfo(center, radius) {
        const windFromAngle = this.toRadians(this.bearingToCanvasAngle(this.trueWind.direction));
        const pixelsPerKnot = 4;
        const arrowLength = this.trueWind.speed * pixelsPerKnot;
        const wX = center + Math.cos(windFromAngle) * radius;
        const wY = center - Math.sin(windFromAngle) * radius;
        this.trueWind.wPos = {
            x: wX,
            y: wY
        };
        const startX = wX;
        const startY = wY;
        const endX = startX - Math.cos(windFromAngle) * arrowLength;
        const endY = startY + Math.sin(windFromAngle) * arrowLength;
        this.trueWind.arrowEndpoint = {
            x: endX,
            y: endY
        };
        this.ctx.save();
        this.ctx.strokeStyle = this.radarFaintGreen;
        this.ctx.fillStyle = this.radarFaintGreen;
        this.ctx.font = `${Math.max(12, radius * 0.08)}px 'IBM Plex Sans Mono', monospace`;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText('W', wX, wY);
        this.ctx.lineWidth = this.strokeWidth('vector');
        this.ctx.beginPath();
        this.ctx.moveTo(startX, startY);
        this.ctx.lineTo(endX, endY);
        this.ctx.stroke();
        const arrowAngle = Math.atan2(endY - startY, endX - startX);
        this.ctx.beginPath();
        this.ctx.moveTo(endX, endY);
        this.ctx.lineTo(endX - 15 * Math.cos(arrowAngle - Math.PI / 6), endY - 15 * Math.sin(arrowAngle - Math.PI / 6));
        this.ctx.lineTo(endX - 15 * Math.cos(arrowAngle + Math.PI / 6), endY - 15 * Math.sin(arrowAngle + Math.PI / 6));
        this.ctx.closePath();
        this.ctx.fill();
        this.ctx.restore();
        if (this.showRelativeMotion) {
            const pixelsPerNm = radius / this.maxRange;
            const timeInHours = this.vectorTimeInMinutes / 60;
            const vectorDistPixels = this.relativeWind.speed * timeInHours * pixelsPerNm;
            const relWindCanvasAngle = this.toRadians(this.bearingToCanvasAngle(this.relativeWind.vectorDirection));
            const vecEndX = center + vectorDistPixels * Math.cos(relWindCanvasAngle);
            const vecEndY = center - vectorDistPixels * Math.sin(relWindCanvasAngle);
            this.ctx.save();
            this.ctx.strokeStyle = this.radarFaintGreen;
            this.ctx.lineWidth = this.strokeWidth('vector');
            this.ctx.setLineDash((0, $759706aefe3a0ba6$export$50ae0dfaa401c539).wind);
            this.ctx.beginPath();
            this.ctx.moveTo(center, center);
            this.ctx.lineTo(vecEndX, vecEndY);
            this.ctx.stroke();
            this.ctx.restore();
        }
    }
    // --- UI Updates ---
    updatePanelsAndRedraw() {
        this.updateOwnShipPanel();
        this.updateDataPanels();
        this.drawRadar();
    }
    updateOwnShipPanel() {
        this._renderEditableField('ownship-crs', `${this.formatBearing(this.ownShip.course)} T`, this.ownShip.course);
        this._renderEditableField('ownship-spd', `${this.ownShip.speed.toFixed(1)} kts`, this.ownShip.speed);
    }
    updateDataPanels() {
        const selectedTrack = this.tracks.find((t)=>t.id === this.selectedTrackId);
        this._setText('track-id', selectedTrack ? selectedTrack.id : '--');
        if (selectedTrack) {
            this._renderEditableField('track-brg', `${this.formatBearing(selectedTrack.bearing)} T`, selectedTrack.bearing);
            this._renderEditableField('track-rng', `${selectedTrack.range.toFixed(1)} nm`, selectedTrack.range);
            this._renderEditableField('track-crs', `${this.formatBearing(selectedTrack.course)} T`, selectedTrack.course);
            this._renderEditableField('track-spd', `${selectedTrack.speed.toFixed(1)} kts`, selectedTrack.speed);
        } else {
            this._setText('track-brg', '--');
            this._setText('track-rng', '--');
            this._setText('track-crs', '--');
            this._setText('track-spd', '--');
        }
        const updateToggleState = (chkId, isOn)=>{
            const chk = document.getElementById(chkId);
            if (chk && chk.checked !== isOn) chk.checked = isOn;
        };
        const showRM = selectedTrack && this.showRelativeMotion;
        updateToggleState('chk-toggle-rm', this.showRelativeMotion);
        // Visual disabled state
        if (this.rmDataContainer) this.rmDataContainer.classList.toggle('disabled', !this.showRelativeMotion);
        this._setText('rm-dir', showRM ? selectedTrack.rm.dir : '--');
        this._setText('rm-spd', showRM ? selectedTrack.rm.spd : '--');
        this._setText('rm-rate', showRM ? selectedTrack.rm.rate : '--');
        this._setText('rm-angle', showRM ? selectedTrack.rm.angle : '--');
        // this._setText('rm-aspect', showRM ? selectedTrack.rm.aspect : '--');
        const showCPA = selectedTrack && this.showCPAInfo && !selectedTrack.hasPassedCPA;
        updateToggleState('chk-toggle-cpa', this.showCPAInfo);
        if (this.cpaDataContainer) this.cpaDataContainer.classList.toggle('disabled', !this.showCPAInfo);
        this._setText('cpa-brg', showCPA ? selectedTrack.cpa.brg : '--');
        this._setText('cpa-rng', showCPA ? selectedTrack.cpa.range : '--');
        this._setText('cpa-time', showCPA ? selectedTrack.cpa.time : '--');
        const showTrueWind = this.showWeather;
        const showRelWind = this.showWeather && this.showRelativeMotion;
        const relWindBearing = (this.relativeWind.vectorDirection - this.ownShip.course + 360) % 360;
        this._setText('wind-true', showTrueWind ? `${this.formatBearing(this.trueWind.direction)} T  ${this.trueWind.speed.toFixed(1)} kts` : '--');
        this._setText('wind-rel', showRelWind ? `${this.formatBearing(relWindBearing)} R  ${this.relativeWind.speed.toFixed(1)} kts` : '--');
    }
    updateButtonStyles() {
        this.btnPlayPause.className = `controlplay ${this.isSimulationRunning ? 'selected' : 'unselected'}`;
        if (this.iconPlay && this.iconPause) {
            this.iconPlay.classList.toggle('d-none', this.isSimulationRunning);
            this.iconPause.classList.toggle('d-none', !this.isSimulationRunning);
        }
        this.btnFuture.className = `control-forward ${this.simulationSpeed > 1 ? 'selected' : 'unselected'}`;
        this.btnPast.className = `control-backward ${this.simulationSpeed < 0 ? 'selected' : 'unselected'}`;
    }
    updateSpeedIndicator() {
        this.ffSpeedIndicator.classList.add('d-none');
        this.revSpeedIndicator.classList.add('d-none');
        if (this.simulationSpeed > 1) {
            const label = this.simulationSpeed === 25 ? '25x' : this.simulationSpeed === 50 ? '50x' : `${this.simulationSpeed}x`;
            this.ffSpeedIndicator.textContent = label;
            this.ffSpeedIndicator.classList.remove('d-none');
        } else if (this.simulationSpeed < 0) {
            const absSpeed = Math.abs(this.simulationSpeed);
            const label = absSpeed === 25 ? '25x' : absSpeed === 50 ? '50x' : `${absSpeed}x`;
            this.revSpeedIndicator.textContent = label;
            this.revSpeedIndicator.classList.remove('d-none');
        }
    }
    updateSimClock() {
        this._setText('sim-clock', this.formatTime(this.simulationElapsed / 3600));
    }
    scaleUI() {
        if (this._destroyed || !this.canvas || !this.ctx) return;
        // Refresh DPR each layout pass so browser zoom / monitor changes re-rasterize crisply.
        this.DPR = window.devicePixelRatio || 1;
        const BASE = 900;
        const containerHeight = this.mainContainer.clientHeight;
        const wrapperWidth = this.radarWrapper.clientWidth;
        let dim = Math.min(wrapperWidth, containerHeight);
        // Viewport Logic
        const isPortrait = window.innerHeight > window.innerWidth;
        // const isMobile = Math.min(window.innerWidth, window.innerHeight) < 793; 
        // Better trigger: match the CSS breakpoint logic roughly, or just check if it's "small"
        const isSmall = window.innerWidth <= 792 || window.innerHeight <= 792;
        document.body.classList.remove('mobile-portrait', 'mobile-landscape', 'tablet');
        if (isSmall) {
            if (isPortrait) document.body.classList.add('mobile-portrait');
            else document.body.classList.add('mobile-landscape');
            // Increase radar canvas size by 15%
            dim = dim * 1.15;
        } else if (window.innerWidth <= 1279) // Tablet tier: between phone and desktop. Keeps the desktop layout but
        // lets CSS tighten card sizing so the radar stays the hero.
        document.body.classList.add('tablet');
        const scale = Math.max(0.7, Math.min(2.0, dim / BASE));
        document.documentElement.style.setProperty('--ui-scale', scale);
        this.uiScaleFactor = scale;
        // size the canvas display to match container square
        this.canvas.style.width = `${dim}px`;
        this.canvas.style.height = `${dim}px`;
        // Canvas resolution depends on its current size in the DOM, which is now controlled by JS.
        const size = dim;
        if (size > 0) {
            this.canvas.width = Math.round(size * this.DPR);
            this.canvas.height = Math.round(size * this.DPR);
            this.staticCanvas.width = this.canvas.width;
            this.staticCanvas.height = this.canvas.height;
            this.staticDirty = true;
        }
        // Scale the drag-start threshold with the radar so picks feel consistent
        // from small phones to large desktop displays.
        this.dragThreshold = Math.max(6 * this.DPR, this.canvas.width / 2 * 0.012);
        // The canvas size changed, so the magnifier's pan bounds did too.
        this._clampViewOffset();
        this.prepareStaticStyles();
        this.markSceneDirty();
    }
    setZoom(scale) {
        this.DPR = window.devicePixelRatio || 1;
        const BASE = 900;
        const clamped = Math.max(0.7, Math.min(2.0, scale));
        document.documentElement.style.setProperty('--ui-scale', clamped);
        this.uiScaleFactor = clamped;
        const size = BASE * clamped;
        this.canvas.style.width = `${size}px`;
        this.canvas.style.height = `${size}px`;
        this.canvas.width = Math.round(size * this.DPR);
        this.canvas.height = Math.round(size * this.DPR);
        this.staticCanvas.width = this.canvas.width;
        this.staticCanvas.height = this.canvas.height;
        this.staticDirty = true;
        this.prepareStaticStyles();
        this.markSceneDirty();
    }
    // --- Interaction Handlers ---
    updateDragTooltip(e) {
        if (!this.draggedItemId) {
            this.dragTooltip.style.display = 'none';
            return;
        }
        let tooltipText = '';
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = (e.clientX - rect.left) * this.DPR;
        const mouseY = (e.clientY - rect.top) * this.DPR;
        const center = this.canvas.width / 2;
        const pixelsPerNm = center * 0.9 / this.maxRange;
        if (this.dragType === 'icon') {
            const dx = mouseX - center;
            const dy = -(mouseY - center);
            const newRange = Math.hypot(dx, dy) / pixelsPerNm;
            const newCanvasAngleRad = Math.atan2(dy, dx);
            const newBearing = this.canvasAngleToBearing(this.toDegrees(newCanvasAngleRad));
            tooltipText = `Brg: ${this.formatBearing(newBearing)} T\nRng: ${newRange.toFixed(1)} nm`;
        } else if (this.dragType === 'vector') {
            const vessel = this.draggedItemId === 'ownShip' ? this.ownShip : this.tracks.find((t)=>t.id === this.draggedItemId);
            if (vessel) {
                const startPoint = vessel.id === 'ownShip' ? {
                    x: center,
                    y: center
                } : this.getTargetCoords(center, center * 0.9, vessel);
                const dx = mouseX - startPoint.x;
                const dy = -(mouseY - startPoint.y);
                const newCanvasAngleRad = Math.atan2(dy, dx);
                const newCourse = this.canvasAngleToBearing(this.toDegrees(newCanvasAngleRad));
                const distOnCanvas = Math.hypot(dx, dy);
                const newSpeed = distOnCanvas / pixelsPerNm / (this.vectorTimeInMinutes / 60);
                tooltipText = `Crs: ${this.formatBearing(newCourse)} T\nSpd: ${newSpeed.toFixed(1)} kts`;
            }
        } else if (this.draggedItemId === 'trueWind') {
            if (this.dragType === 'windDirection') tooltipText = `Dir: ${this.formatBearing(this.trueWind.direction)} T`;
            else if (this.dragType === 'windSpeed') tooltipText = `Spd: ${this.trueWind.speed.toFixed(1)} kts`;
        }
        if (tooltipText) {
            if (this.draggedItemId === 'ownShip' && this.dragType === 'vector') this.dragTooltip.style.color = this.radarWhite;
            else this.dragTooltip.style.color = this.radarGreen;
            this.dragTooltip.innerText = tooltipText;
            this.dragTooltip.style.display = 'block';
            this.dragTooltip.style.transform = `translate(${e.clientX - this.dragTooltip.offsetWidth - 10}px, ${e.clientY - this.dragTooltip.offsetHeight - 10}px)`;
        } else this.dragTooltip.style.display = 'none';
    }
    handlePointerDown(e) {
        if (e.button !== 0 && e.buttons !== undefined && e.buttons !== 1) return;
        if (this.canvas.setPointerCapture && e.pointerId !== undefined) try {
            this.canvas.setPointerCapture(e.pointerId);
        } catch (err) {}
        const rect = this.canvas.getBoundingClientRect();
        const sx = (e.clientX - rect.left) * this.DPR;
        const sy = (e.clientY - rect.top) * this.DPR;
        // A second pointer turns the gesture into a pinch (magnify + pan).
        this._activePointers.set(e.pointerId ?? 'mouse', {
            x: sx,
            y: sy
        });
        if (this._activePointers.size >= 2) {
            this._enterPinch();
            return;
        }
        // Single-finger interactions operate in world (un-zoomed) coordinates.
        const { x: mouseX, y: mouseY } = this._screenToWorld(sx, sy);
        const item = this.getInteractiveItemAt(mouseX, mouseY);
        this.pointerDownPos = {
            x: mouseX,
            y: mouseY
        };
        if (item) {
            this.canvas.style.cursor = 'grabbing';
            this.pendingDragId = item.id;
            this.pendingDragType = item.type;
            this.lastMousePos = {
                x: mouseX,
                y: mouseY
            };
            if (item.id !== 'ownShip' && item.id !== 'trueWind') this.selectedTrackId = item.id;
            this.hoveredTrackId = item.id;
            this.markSceneDirty();
        } else {
            this.pendingDragId = null;
            this.pendingDragType = null;
            this.hoveredTrackId = null;
            // Empty water: arm a long-press to drop a new contact here.
            this._armLongPress(mouseX, mouseY);
        }
    }
    _armLongPress(x, y) {
        this._clearLongPress();
        this._longPress = {
            x: x,
            y: y,
            timer: setTimeout(()=>this._fireLongPress(), this.LONG_PRESS_MS)
        };
    }
    _clearLongPress() {
        if (this._longPress) {
            clearTimeout(this._longPress.timer);
            this._longPress = null;
        }
    }
    // Long-press fired: promote the pending press into a ghost contact at the
    // pressed bearing/range (inverse of getTargetCoords). Dragging then sets its
    // course/speed; release commits it via addTrack.
    _fireLongPress() {
        if (!this._longPress) return;
        const center = this.canvas.width / 2;
        const pixelsPerNm = center * 0.9 / this.maxRange;
        const dx = this._longPress.x - center;
        const dy = -(this._longPress.y - center);
        const bearing = this.canvasAngleToBearing(this.toDegrees(Math.atan2(dy, dx)));
        this._ghostContact = {
            x: this._longPress.x,
            y: this._longPress.y,
            bearing: bearing,
            range: Math.hypot(dx, dy) / pixelsPerNm,
            course: bearing,
            speed: 10
        };
        this._longPress = null;
        this.canvas.style.cursor = 'grabbing';
        this.markSceneDirty();
    }
    _updateGhostVector(mouseX, mouseY) {
        const g = this._ghostContact;
        const center = this.canvas.width / 2;
        const pixelsPerNm = center * 0.9 / this.maxRange;
        const dx = mouseX - g.x;
        const dy = -(mouseY - g.y);
        const dist = Math.hypot(dx, dy);
        if (dist > this.dragThreshold) {
            g.course = this.canvasAngleToBearing(this.toDegrees(Math.atan2(dy, dx)));
            g.speed = dist / pixelsPerNm / (this.vectorTimeInMinutes / 60);
        }
        this.markSceneDirty();
    }
    _drawGhost(center, radius) {
        const g = this._ghostContact;
        if (!g) return;
        const ctx = this.ctx;
        const size = Math.max(11, radius * 0.038);
        ctx.save();
        ctx.globalAlpha = 0.6;
        ctx.strokeStyle = this.radarWhite;
        if (g.speed > 0) {
            const pixelsPerNm = center * 0.9 / this.maxRange;
            const lenPx = g.speed * (this.vectorTimeInMinutes / 60) * pixelsPerNm;
            const ang = this.toRadians(this.bearingToCanvasAngle(g.course));
            ctx.lineWidth = this.strokeWidth('vector');
            ctx.setLineDash([
                6 * this.DPR,
                6 * this.DPR
            ]);
            ctx.beginPath();
            ctx.moveTo(g.x, g.y);
            ctx.lineTo(g.x + lenPx * Math.cos(ang), g.y - lenPx * Math.sin(ang));
            ctx.stroke();
            ctx.setLineDash([]);
        }
        ctx.lineWidth = this.strokeWidth('object');
        ctx.beginPath();
        ctx.rect(g.x - size / 2, g.y - size / 2, size, size);
        ctx.stroke();
        ctx.restore();
    }
    // Convert a screen (device-px) point to world coordinates by inverting the
    // view camera, so hit-testing and drag math stay in un-zoomed space.
    _screenToWorld(sx, sy) {
        return {
            x: (sx - this.viewOffset.x) / this.viewScale,
            y: (sy - this.viewOffset.y) / this.viewScale
        };
    }
    // Keep the magnified scene covering the viewport so panning can't expose a gap;
    // at scale 1 this forces the offset to 0, recentering ownship.
    _clampViewOffset() {
        const minX = this.canvas.width * (1 - this.viewScale);
        const minY = this.canvas.height * (1 - this.viewScale);
        this.viewOffset.x = Math.min(0, Math.max(minX, this.viewOffset.x));
        this.viewOffset.y = Math.min(0, Math.max(minY, this.viewOffset.y));
    }
    // Two fingers down: cancel any single-finger interaction (long-press, ghost,
    // or in-progress drag) and snapshot the pinch midpoint + distance as a baseline.
    _enterPinch() {
        this._pinching = true;
        this._pinchPrev = {
            mid: this._pinchMid(),
            dist: this._pinchDistance()
        };
        this._clearLongPress();
        this._ghostContact = null;
        if (this.draggedItemId && this.draggedItemId !== 'ownShip' && this.draggedItemId !== 'trueWind') this.engineClient.dragEnd(this.draggedItemId);
        this.draggedItemId = null;
        this.dragType = null;
        this.pendingDragId = null;
        this.pendingDragType = null;
        this.ownShip.dragCourse = null;
        this.ownShip.dragSpeed = null;
        this.dragTooltip.style.display = 'none';
        this.orderTooltip.style.display = 'none';
        this.canvas.style.cursor = 'default';
        this.markSceneDirty();
    }
    _pinchDistance() {
        const pts = [
            ...this._activePointers.values()
        ];
        if (pts.length < 2) return 0;
        return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
    }
    _pinchMid() {
        const pts = [
            ...this._activePointers.values()
        ];
        if (pts.length < 2) return {
            x: 0,
            y: 0
        };
        return {
            x: (pts[0].x + pts[1].x) / 2,
            y: (pts[0].y + pts[1].y) / 2
        };
    }
    // Map-style pinch: spreading fingers magnifies, pinching together zooms back
    // out, and the midpoint motion pans. Anchoring the world point under the
    // previous midpoint to the current midpoint handles zoom + pan in one step.
    _updatePinch() {
        if (this._activePointers.size < 2 || !this._pinchPrev || this._pinchPrev.dist <= 0) return;
        const mid = this._pinchMid();
        const dist = this._pinchDistance();
        const wx = (this._pinchPrev.mid.x - this.viewOffset.x) / this.viewScale;
        const wy = (this._pinchPrev.mid.y - this.viewOffset.y) / this.viewScale;
        this.viewScale = Math.max(1, Math.min(this.MAX_VIEW_SCALE, this.viewScale * (dist / this._pinchPrev.dist)));
        this.viewOffset.x = mid.x - wx * this.viewScale;
        this.viewOffset.y = mid.y - wy * this.viewScale;
        this._clampViewOffset();
        this._pinchPrev = {
            mid: mid,
            dist: dist
        };
        this.markSceneDirty();
    }
    handlePointerUp(e) {
        if (this.canvas.releasePointerCapture && e && e.pointerId !== undefined) try {
            this.canvas.releasePointerCapture(e.pointerId);
        } catch (err) {
        // ignore if not captured
        }
        this.canvas.style.cursor = 'grab';
        // Pinch: drop this finger; stay latched until all fingers lift so a leftover
        // finger never starts a drag or long-press mid-gesture.
        this._activePointers.delete(e.pointerId ?? 'mouse');
        if (this._pinching) {
            if (this._activePointers.size === 0) this._pinching = false;
            this._pinchPrev = null;
            return;
        }
        // Long-press placement resolves before the normal drag-commit path.
        if (this._longPress) this._clearLongPress(); // released as a plain tap
        if (this._ghostContact) {
            const g = this._ghostContact;
            this._ghostContact = null;
            this.selectedTrackId = this.addTrack({
                bearing: g.bearing,
                range: g.range,
                course: g.course,
                speed: g.speed,
                isUserControlled: true
            });
            this.markSceneDirty();
            return;
        }
        if (this.draggedItemId === 'ownShip' && this.dragType === 'vector') {
            if (this.ownShip.dragCourse !== null && this.ownShip.dragSpeed !== null) {
                this.ownShip.orderedCourse = this.ownShip.dragCourse;
                this.ownShip.orderedSpeed = this.ownShip.dragSpeed;
                this.engineClient.editOwnShip('orderedCourse', this.ownShip.orderedCourse);
                this.engineClient.editOwnShip('orderedSpeed', this.ownShip.orderedSpeed);
            }
        } else if (this.draggedItemId && this.draggedItemId !== 'trueWind') {
            // Flush the final dragged value so the worker's authoritative state
            // matches the drop point exactly (prevents a snap-back), then
            // release the lock so integration resumes for this track.
            this._flushDragUpdate();
            this.engineClient.dragEnd(this.draggedItemId);
        }
        this.ownShip.dragCourse = null;
        this.ownShip.dragSpeed = null;
        this.draggedItemId = null;
        this.dragType = null;
        this.pendingDragId = null;
        this.pendingDragType = null;
        this.dragTooltip.style.display = 'none';
        this.orderTooltip.style.display = 'none';
        this.markSceneDirty();
    }
    handleContainerPointerMove(e) {
    // Prevent crash. Implement dragging-to-pan later if needed.
    }
    handleContainerPointerDown(e) {
    // Optional: track pointer start position for dragging container
    }
    handleContainerPointerUp(e) {
    // Optional: reset state if you want dragging or container interaction
    }
    handlePointerMove(e) {
        const rect = this.canvas.getBoundingClientRect();
        const sx = (e.clientX - rect.left) * this.DPR;
        const sy = (e.clientY - rect.top) * this.DPR;
        if (this._pinching) {
            const key = e.pointerId ?? 'mouse';
            if (this._activePointers.has(key)) this._activePointers.set(key, {
                x: sx,
                y: sy
            });
            this._updatePinch();
            return;
        }
        const { x: mouseX, y: mouseY } = this._screenToWorld(sx, sy);
        // Long-press placement: moving before the timer fires cancels it (it was a
        // scroll/drag, not a press); once a ghost exists, the drag sets its vector.
        if (this._longPress) {
            if (Math.hypot(mouseX - this._longPress.x, mouseY - this._longPress.y) > this.dragThreshold) this._clearLongPress();
            return;
        }
        if (this._ghostContact) {
            this._updateGhostVector(mouseX, mouseY);
            return;
        }
        if (this.pendingDragId && !this.draggedItemId) {
            const dx0 = mouseX - this.pointerDownPos.x;
            const dy0 = mouseY - this.pointerDownPos.y;
            if (Math.hypot(dx0, dy0) > this.dragThreshold) {
                this.draggedItemId = this.pendingDragId;
                this.dragType = this.pendingDragType;
                if (this.draggedItemId === 'ownShip' && this.dragType === 'vector') {
                    this.ownShip.dragCourse = this.ownShip.orderedCourse;
                    this.ownShip.dragSpeed = this.ownShip.orderedSpeed;
                } else if (this.draggedItemId !== 'trueWind') // Lock the track in the worker so it is not integrated while
                // the user drags it; the view updates its position directly.
                this.engineClient.dragStart(this.draggedItemId);
            }
        }
        if (this.draggedItemId) {
            this.updateDragTooltip(e);
            const center = this.canvas.width / 2;
            const pixelsPerNm = center * 0.9 / this.maxRange;
            if (this.draggedItemId === 'trueWind') {
                const dx = mouseX - center;
                const dy = -(mouseY - center);
                const newCanvasAngleRad = Math.atan2(dy, dx);
                if (this.dragType === 'windDirection') this.trueWind.direction = this.canvasAngleToBearing(this.toDegrees(newCanvasAngleRad));
                else if (this.dragType === 'windSpeed') {
                    const pixelsPerKnot = 4;
                    const windFromAngle = this.toRadians(this.bearingToCanvasAngle(this.trueWind.direction));
                    const radius = center * 0.9;
                    const mouseVecX = mouseX - center;
                    const mouseVecY = -(mouseY - center);
                    const windVecX = Math.cos(windFromAngle);
                    const windVecY = Math.sin(windFromAngle);
                    const projectedLength = mouseVecX * windVecX + mouseVecY * windVecY;
                    const arrowPixelLength = projectedLength - radius;
                    this.trueWind.speed = Math.max(0, -arrowPixelLength / pixelsPerKnot);
                }
                this.markSceneDirty();
            } else if (this.dragType === 'icon') {
                const track = this.tracks.find((t)=>t.id === this.draggedItemId);
                if (track) {
                    const deltaX_pixels = mouseX - this.lastMousePos.x;
                    const deltaY_pixels = mouseY - this.lastMousePos.y;
                    const deltaX_nm = deltaX_pixels / pixelsPerNm;
                    const deltaY_nm = deltaY_pixels / pixelsPerNm;
                    track.x += deltaX_nm;
                    track.y -= deltaY_nm;
                    this._queueDragUpdate(track.id, 'position', {
                        x: track.x,
                        y: track.y
                    });
                    this.markSceneDirty();
                }
            } else if (this.dragType === 'vector') {
                const timeInHours = this.vectorTimeInMinutes / 60;
                const vessel = this.draggedItemId === 'ownShip' ? this.ownShip : this.tracks.find((t)=>t.id === this.draggedItemId);
                const startPoint = vessel.id === 'ownShip' ? {
                    x: center,
                    y: center
                } : this.getTargetCoords(center, center * 0.9, vessel);
                const dx = mouseX - startPoint.x;
                const dy = -(mouseY - startPoint.y);
                const newCanvasAngleRad = Math.atan2(dy, dx);
                const newCourse = this.canvasAngleToBearing(this.toDegrees(newCanvasAngleRad));
                const distOnCanvas = Math.sqrt(dx * dx + dy * dy);
                const newSpeed = distOnCanvas / pixelsPerNm / timeInHours;
                if (vessel.id === 'ownShip') {
                    this.ownShip.dragCourse = newCourse;
                    this.ownShip.dragSpeed = Math.max(0, newSpeed);
                } else {
                    vessel.course = newCourse;
                    vessel.speed = Math.max(2, newSpeed);
                    this._queueDragUpdate(vessel.id, 'vector', {
                        course: vessel.course,
                        speed: vessel.speed
                    });
                }
                this.markSceneDirty();
            }
            this.lastMousePos = {
                x: mouseX,
                y: mouseY
            };
        } else {
            let item = null;
            if (!this.pendingDragId) {
                item = this.getInteractiveItemAt(mouseX, mouseY);
                this.canvas.style.cursor = item ? 'grab' : 'default';
            }
            const newHoverId = item ? item.id : null;
            if (newHoverId !== this.hoveredTrackId) {
                this.hoveredTrackId = newHoverId;
                this.markSceneDirty();
            }
        }
    }
    getInteractiveItemAt(mouseX, mouseY) {
        const center = this.canvas.width / 2;
        const radius = center * 0.9;
        const hitTolerance = Math.max(12 * this.DPR, radius * 0.030);
        const minVecPickDistance = Math.max(20 * this.DPR, radius * 0.055);
        if (this.showWeather) {
            const distToW = Math.sqrt((mouseX - this.trueWind.wPos.x) ** 2 + (mouseY - this.trueWind.wPos.y) ** 2);
            if (distToW < hitTolerance) return {
                type: 'windDirection',
                id: 'trueWind'
            };
            const distToArrowhead = Math.sqrt((mouseX - this.trueWind.arrowEndpoint.x) ** 2 + (mouseY - this.trueWind.arrowEndpoint.y) ** 2);
            if (distToArrowhead < hitTolerance) return {
                type: 'windSpeed',
                id: 'trueWind'
            };
        }
        for (const track of this.tracks){
            const { x: x, y: y } = this.getTargetCoords(center, radius, track);
            const size = Math.max(11, radius * 0.038) * 1.5;
            if (mouseX > x - size / 2 && mouseX < x + size / 2 && mouseY > y - size / 2 && mouseY < y + size / 2) return {
                type: 'icon',
                id: track.id
            };
        }
        const allVessels = [
            this.ownShip,
            ...this.tracks
        ];
        for (const vessel of allVessels){
            if (!vessel.vectorEndpoint) continue;
            const startPt = vessel.id === 'ownShip' ? {
                x: center,
                y: center
            } : this.getTargetCoords(center, radius, vessel);
            const distFromStart = Math.hypot(mouseX - startPt.x, mouseY - startPt.y);
            if (distFromStart < minVecPickDistance) continue;
            const endPt = vessel.vectorEndpoint;
            const dist = this.distToSegment({
                x: mouseX,
                y: mouseY
            }, startPt, endPt);
            if (dist < hitTolerance) return {
                type: 'vector',
                id: vessel.id
            };
        }
        return null;
    }
    // --- Control Button Actions ---
    toggleVectorTime() {
        const currentIndex = this.vectorTimes.indexOf(this.vectorTimeInMinutes);
        this.vectorTimeInMinutes = this.vectorTimes[(currentIndex + 1) % this.vectorTimes.length];
        this._setText('btn-vector-time', this.vectorTimeInMinutes + ' min');
        this.markSceneDirty();
    }
    // --- Range Toggle ---
    _updateRangeButtonClass() {
        if (!this.btnRange) return;
        this.btnRange.classList.remove('range-3', 'range-6', 'range-12', 'range-24');
        this.btnRange.classList.add(`range-${Math.round(this.maxRange)}`);
    }
    // Set the range scale by index, clamped to the available scales. Shared by the
    // range button (wraps) and the pinch gesture (clamps, no wrap).
    setRangeIndex(i) {
        const clamped = Math.max(0, Math.min(this.rangeScales.length - 1, i));
        if (clamped === this.rangeIndex) return;
        this.rangeIndex = clamped;
        this.maxRange = this.rangeScales[clamped];
        this._updateRangeButtonClass();
        this.staticDirty = true;
        this.markSceneDirty();
    }
    toggleRange() {
        this.setRangeIndex((this.rangeIndex + 1) % this.rangeScales.length);
    }
    toggleWeather() {
        this.showWeather = !this.showWeather;
        this.markSceneDirty();
        this.updateDataPanels();
    }
    toggleRelativeMotion() {
        this.showRelativeMotion = !this.showRelativeMotion;
        this.markSceneDirty();
        this._scheduleUIUpdate();
    }
    toggleCPAInfo() {
        this.showCPAInfo = !this.showCPAInfo;
        this.markSceneDirty();
        this._scheduleUIUpdate();
    }
    togglePlayPause() {
        const wasRunning = this.isSimulationRunning;
        this.isSimulationRunning = !this.isSimulationRunning;
        this.simulationSpeed = 1;
        this.engineClient.setSpeed(this.simulationSpeed);
        this.engineClient.setRunning(this.isSimulationRunning);
        this.updateButtonStyles();
        this.updateSpeedIndicator();
        if (!wasRunning) this.startGameLoop();
    }
    fastForward() {
        const cycle = [
            1,
            ...this.ffSpeeds
        ];
        if (!this.isSimulationRunning || this.simulationSpeed < 0) {
            this.simulationSpeed = this.ffSpeeds[0];
            this.isSimulationRunning = true;
        } else {
            let idx = cycle.indexOf(this.simulationSpeed);
            idx = (idx + 1) % cycle.length;
            this.simulationSpeed = cycle[idx];
            this.isSimulationRunning = true;
        }
        this.engineClient.setSpeed(this.simulationSpeed);
        this.engineClient.setRunning(true);
        this.updateButtonStyles();
        this.updateSpeedIndicator();
        this.startGameLoop();
    }
    rewind() {
        const cycle = [
            1,
            ...this.revSpeeds
        ];
        if (!this.isSimulationRunning || this.simulationSpeed > 0) {
            this.simulationSpeed = this.revSpeeds[0];
            this.isSimulationRunning = true;
        } else {
            let idx = cycle.indexOf(this.simulationSpeed);
            idx = (idx + 1) % cycle.length;
            this.simulationSpeed = cycle[idx];
            this.isSimulationRunning = true;
        }
        this.engineClient.setSpeed(this.simulationSpeed);
        this.engineClient.setRunning(true);
        this.updateButtonStyles();
        this.updateSpeedIndicator();
        this.startGameLoop();
    }
    addTrack(opts = {}) {
        const existingNums = this.tracks.map((t)=>parseInt(t.id, 10)).sort((a, b)=>a - b);
        let newNum = 1;
        while(existingNums.includes(newNum))newNum++;
        const newId = String(newNum).padStart(4, '0');
        const initialBearing = opts.bearing !== undefined ? opts.bearing : Math.random() * 360;
        const initialRange = opts.range !== undefined ? opts.range : this.maxRange * (0.1 + Math.random() * 0.8);
        const newTrack = {
            id: newId,
            initialBearing: initialBearing,
            initialRange: initialRange,
            course: opts.course !== undefined ? opts.course : Math.random() * 360,
            speed: opts.speed !== undefined ? opts.speed : 2 + Math.random() * 13
        };
        if (opts.isUserControlled) newTrack.isUserControlled = true;
        newTrack.x = this.ownShip.x + newTrack.initialRange * Math.sin(this.toRadians(newTrack.initialBearing));
        newTrack.y = this.ownShip.y + newTrack.initialRange * Math.cos(this.toRadians(newTrack.initialBearing));
        this.tracks.push(newTrack);
        this.engineClient.addTrack(this._serializeTrack(newTrack));
        this.selectedTrackId = newId;
        this.calculateAllData(newTrack);
        this.updatePanelsAndRedraw();
        this.markSceneDirty();
        return newId;
    }
    dropTrack() {
        if (this.selectedTrackId === null) return;
        const trackIndex = this.tracks.findIndex((t)=>t.id === this.selectedTrackId);
        if (trackIndex > -1) {
            const removedId = this.tracks[trackIndex].id;
            this.tracks.splice(trackIndex, 1);
            this.engineClient.dropTrack(removedId);
            if (this.tracks.length > 0) {
                const newIndex = Math.max(0, trackIndex - 1);
                this.selectedTrackId = this.tracks[newIndex].id;
            } else this.selectedTrackId = null;
            this.updatePanelsAndRedraw();
            this.markSceneDirty();
        }
    }
    togglePolarPlot() {
        this.showPolarPlot = !this.showPolarPlot;
        this.staticDirty = true;
        this.markSceneDirty();
    }
    toggleTrackIds() {
        this.showTrackIds = !this.showTrackIds;
        this.markSceneDirty();
    }
    async toggleFullScreen() {
        try {
            if (!$8b46766143819e9f$var$fsApi.isActive()) await $8b46766143819e9f$var$fsApi.request();
            else await $8b46766143819e9f$var$fsApi.exit();
        } catch (err) {
            console.error('Fullscreen error', err);
        }
    }
    _syncFullscreenUI() {
        const entering = !!$8b46766143819e9f$var$fsApi.isActive();
        this.btnFullscreen.setAttribute('data-state', entering ? 'exit' : 'enter');
        this.btnFullscreen.title = entering ? 'Exit full screen' : 'Enter full screen';
        this.btnFullscreen.ariaLabel = this.btnFullscreen.title;
    }
    showHelpModal() {
        this.helpModal.style.display = 'flex';
        const left = (window.innerWidth - this.helpModal.offsetWidth) / 2;
        const top = (window.innerHeight - this.helpModal.offsetHeight) / 2;
        this.helpModal.style.left = `${Math.max(0, left)}px`;
        this.helpModal.style.top = `${Math.max(0, top)}px`;
    }
    hideHelpModal() {
        this.helpModal.style.display = 'none';
    }
    // --- Utility & Helper Methods ---
    toRadians(degrees) {
        return degrees * Math.PI / 180;
    }
    toDegrees(radians) {
        return (radians * 180 / Math.PI + 360) % 360;
    }
    bearingToCanvasAngle(bearing) {
        return (450 - bearing) % 360;
    }
    canvasAngleToBearing(angle) {
        return (450 - angle) % 360;
    }
    formatBearing(num) {
        return Math.round(num).toString().padStart(3, '0');
    }
    formatTime(hours) {
        if (hours < 0 || !isFinite(hours)) return '--:--:--';
        const totalSeconds = hours * 3600;
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor(totalSeconds % 3600 / 60);
        const s = Math.floor(totalSeconds % 60);
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    getRelativeQuarter(cpaBearing, ownShipCourse) {
        const relativeBearing = (cpaBearing - ownShipCourse + 360) % 360;
        if (relativeBearing >= 0 && relativeBearing < 90) return 'STBD BOW';
        if (relativeBearing >= 90 && relativeBearing < 180) return 'STBD QTR';
        if (relativeBearing >= 180 && relativeBearing < 270) return 'PORT QTR';
        return 'PORT BOW';
    }
    getAspect(targetAngle) {
        if (targetAngle >= 337.5 || targetAngle < 22.5) return 'BOW';
        if (targetAngle >= 22.5 && targetAngle < 67.5) return 'STBD BOW';
        if (targetAngle >= 67.5 && targetAngle < 112.5) return 'STBD BM';
        if (targetAngle >= 112.5 && targetAngle < 157.5) return 'STBD QTR';
        if (targetAngle >= 157.5 && targetAngle < 202.5) return 'STERN';
        if (targetAngle >= 202.5 && targetAngle < 247.5) return 'PORT QTR';
        if (targetAngle >= 247.5 && targetAngle < 292.5) return 'PORT BM';
        if (targetAngle >= 292.5 && targetAngle < 337.5) return 'PORT BOW';
        return 'N/A';
    }
    getBearingRate(relativeVelocity, targetPosition, range) {
        const crossProduct = targetPosition.x * relativeVelocity.y - targetPosition.y * relativeVelocity.x;
        if (range < 0.01) return '0.00 STEADY';
        const bearingRateRadPerHour = crossProduct / (range * range);
        const bearingRateDpm = bearingRateRadPerHour * 180 / Math.PI / 60;
        let direction;
        if (Math.abs(bearingRateDpm) < 0.01) direction = 'STEADY';
        else if (bearingRateDpm > 0) direction = 'LEFT';
        else direction = 'RIGHT';
        return `${Math.abs(bearingRateDpm).toFixed(2)} ${direction}`;
    }
    distToSegment(p, v, w) {
        const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
        if (l2 == 0) return Math.sqrt((p.x - v.x) ** 2 + (p.y - v.y) ** 2);
        let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
        t = Math.max(0, Math.min(1, t));
        return Math.sqrt((p.x - (v.x + t * (w.x - v.x))) ** 2 + (p.y - (v.y + t * (w.y - v.y))) ** 2);
    }
    prepareStaticStyles() {
        if (!this.ctx) return;
        this.ctx.strokeStyle = this.radarFaintGreen;
        this.ctx.fillStyle = this.radarFaintGreen;
        this.ctx.font = `${Math.max(12, this.canvas.width * 0.04)}px 'IBM Plex Sans Mono', monospace`;
    }
} // --- Application Entry Point ---
 // function enforceLandscape() {
 //     const warning = document.getElementById('orientation-warning');
 //     const isMobile = /Mobi|Android/i.test(navigator.userAgent);
 //     if (!isMobile || window.matchMedia('(orientation: landscape)').matches) {
 //         document.body.classList.remove('portrait');
 //         if (warning) warning.style.display = 'none';
 //     } else {
 //         document.body.classList.add('portrait');
 //         if (warning) warning.style.display = 'flex';
 //     }
 // }
 // document.addEventListener('DOMContentLoaded', () => {
 //     if (window.navigator.standalone) {
 //         document.body.classList.add('pwa-standalone');
 //     }
 //     const sim = new Simulator();
 //     enforceLandscape();
 //     window.addEventListener('orientationchange', enforceLandscape);
 //     window.addEventListener('resize', enforceLandscape);
 // });
 // Register service worker for offline support
 // if ('serviceWorker' in navigator) {
 //     window.addEventListener('load', () => {
 //         navigator.serviceWorker.register('/sw.js')
 //             .then(reg => console.log('Service Worker registered', reg))
 //             .catch(err => console.error('Service Worker registration failed:', err));
 //     });
 // }


// Register service worker if available
if ('serviceWorker' in navigator) window.addEventListener('load', ()=>{
    navigator.serviceWorker.register(new URL('./service-worker.js', window.location.href));
});
const $77ac944da51b7354$var$ONBOARDING_SEEN_KEY = 'maneuver.onboarding.seen.v1';
/**
 * @class GameManager
 * Handles high-level application state and mode selection.
 */ class $77ac944da51b7354$var$GameManager {
    constructor(){
        this.menuOverlay = document.getElementById('main-menu');
        this.btnSimulator = document.getElementById('btn-simulator');
        this.onboardingOverlay = document.getElementById('onboarding-overlay');
        this.btnHelp = document.getElementById('btn-help');
        this.onboardingDismiss = document.getElementById('onboarding-dismiss');
        this._onboardingShownThisSession = false;
        // Bind methods
        this.startSimulatorMode = this.startSimulatorMode.bind(this);
        this._onOnboardingKeydown = this._onOnboardingKeydown.bind(this);
        this._attachListeners();
        this._checkURLParams();
    }
    _attachListeners() {
        this.btnSimulator?.addEventListener('click', this.startSimulatorMode);
        // Onboarding: help button re-opens it; backdrop click and "Got it" dismiss it.
        this.btnHelp?.addEventListener('click', ()=>this.showOnboarding());
        this.onboardingDismiss?.addEventListener('click', ()=>this.hideOnboarding());
        this.onboardingOverlay?.addEventListener('click', (e)=>{
            if (e.target === this.onboardingOverlay) this.hideOnboarding();
        });
        // Main Menu navigation via Logo
        const logo = document.querySelector('.maneuverlogo');
        if (logo) {
            logo.style.cursor = 'pointer';
            // pointerup fires reliably on touch (a synthesized click can be suppressed
            // on mobile), so the logo opens the main menu on phones as well as desktop.
            logo.addEventListener('pointerup', ()=>{
                this.showMenu();
                if (window.sim && window.sim.isSimulationRunning) window.sim.togglePlayPause();
            });
        }
    }
    _checkURLParams() {
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');
        if (mode === 'simulator') this.startSimulatorMode();
    }
    hideMenu() {
        this.menuOverlay.classList.add('hidden');
    }
    showMenu() {
        this.menuOverlay.classList.remove('hidden');
    }
    startSimulatorMode() {
        this.hideMenu();
        this.initSimulator({
            mode: 'simulator'
        });
        this._maybeShowOnboarding();
    }
    initSimulator(config) {
        if (window.sim) window.sim.destroy();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(()=>{
            window.sim = new (0, $8b46766143819e9f$export$b14c74960ae6f55e)(config);
        });
        else window.sim = new (0, $8b46766143819e9f$export$b14c74960ae6f55e)(config);
    }
    _maybeShowOnboarding() {
        let seen = false;
        try {
            seen = localStorage.getItem($77ac944da51b7354$var$ONBOARDING_SEEN_KEY) === '1';
        } catch (_) {
            // Private mode / storage disabled: fall back to once per session.
            seen = this._onboardingShownThisSession;
        }
        if (!seen) this.showOnboarding();
    }
    showOnboarding() {
        if (!this.onboardingOverlay) return;
        this._onboardingShownThisSession = true;
        this.onboardingOverlay.classList.remove('hidden');
        document.addEventListener('keydown', this._onOnboardingKeydown);
    }
    hideOnboarding(persist = true) {
        if (!this.onboardingOverlay) return;
        this.onboardingOverlay.classList.add('hidden');
        document.removeEventListener('keydown', this._onOnboardingKeydown);
        if (persist) try {
            localStorage.setItem($77ac944da51b7354$var$ONBOARDING_SEEN_KEY, '1');
        } catch (_) {
        // Ignore write failures (private mode).
        }
    }
    _onOnboardingKeydown(e) {
        if (e.key === 'Escape') this.hideOnboarding();
    }
}
// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', ()=>{
    // If embedded or waiting for specific scripts (like existing logic checked window.TypeForm),
    // adapt here. For this refactor, we assume direct control.
    window.gameManager = new $77ac944da51b7354$var$GameManager();
// Existing embeds logic from previous index.html code can be adapted if actually needed,
// but requirements suggest we control the entry via Main Menu now. 
});


//# sourceMappingURL=path0.0ae27ec5.js.map
